import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import {
  Menu, Search, RefreshCw, Star, Trash2, Mail, Send, FileText,
  ShieldAlert, Trash, Plus, Archive, ChevronLeft, ChevronRight,
  MoreVertical, CheckSquare, Square, Paperclip, X, Minimize2,
  Maximize2, ArrowLeft, CornerUpLeft, CornerUpRight, Download,
  Settings, LogOut, Sun, Moon, Shield, FolderPlus, Folder,
  HelpCircle, Eye, EyeOff, Check, AlertCircle, Clock, Calendar as CalendarIcon,
  Users, UserPlus, Phone, Tag, Lock, Key, Cloud, Laptop, Sliders, Edit3,
  ArrowLeftRight, Reply, Heart, Sparkles, CheckCircle2, Copy, HardDrive,
  Filter, ShieldCheck, Database, Smartphone, Globe, Bold, Italic,
  Underline, List, ListOrdered, Link2, Printer, FileDown, Bookmark,
  Zap, Palette, Image, Camera, UploadCloud, ExternalLink, Info, ChevronDown, ChevronUp
} from 'lucide-react';

const LazyAdminDashboard = lazy(() => import('./AdminDashboard'));

const API_BASE = window.location.port === '5173' ? 'http://localhost:5006/api' : '/api';

const LABEL_COLORS = [
  { hex: '#ef4444', name: 'Red' },
  { hex: '#f97316', name: 'Orange' },
  { hex: '#f59e0b', name: 'Amber' },
  { hex: '#10b981', name: 'Emerald' },
  { hex: '#14b8a6', name: 'Teal' },
  { hex: '#0ea5e9', name: 'Sky' },
  { hex: '#3b82f6', name: 'Blue' },
  { hex: '#6366f1', name: 'Indigo' },
  { hex: '#8b5cf6', name: 'Purple' },
  { hex: '#d946ef', name: 'Fuchsia' },
  { hex: '#ec4899', name: 'Pink' },
  { hex: '#64748b', name: 'Slate' }
];

// Gmail Material Avatar Colors
const GOOGLE_AVATAR_COLORS = [
  '#ea4335', '#4285f4', '#34a853', '#fbbc04',
  '#9334e8', '#e8710a', '#129eaf', '#c2185b',
  '#00897b', '#5c6bc0', '#8e24aa', '#3949ab',
  '#d81b60', '#00acc1', '#43a047', '#fb8c00'
];

function getEmailColor(str = '') {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return GOOGLE_AVATAR_COLORS[Math.abs(hash) % GOOGLE_AVATAR_COLORS.length];
}

function getBrandLogoUrl(email = '') {
  if (!email || !email.includes('@')) return null;
  const domain = email.split('@')[1].toLowerCase();
  const brandDomains = [
    'google.com', 'github.com', 'microsoft.com', 'apple.com', 'amazon.com',
    'netflix.com', 'paypal.com', 'stripe.com', 'twitter.com', 'x.com',
    'facebook.com', 'meta.com', 'linkedin.com', 'discord.com', 'slack.com',
    'spotify.com', 'uber.com', 'airbnb.com', 'dropbox.com', 'notion.so',
    'digitalocean.com', 'cloudflare.com', 'gitlab.com', 'docker.com',
    'medium.com', 'reddit.com', 'vercel.com', 'supabase.com', 'heroku.com'
  ];
  if (brandDomains.includes(domain)) {
    return `https://unavatar.io/${domain}?fallback=false`;
  }
  return null;
}

export function GmailAvatar({ email = '', name = '', customAvatarUrl = '', size = 36, style = {} }) {
  const [loadError, setLoadError] = useState(false);
  const initial = (name || email || '?').trim().charAt(0).toUpperCase();
  const bgColor = getEmailColor(email || name);

  useEffect(() => {
    setLoadError(false);
  }, [email, customAvatarUrl]);

  let avatarSrc = customAvatarUrl;
  if (!avatarSrc && email) {
    const brandLogo = getBrandLogoUrl(email);
    if (brandLogo) {
      avatarSrc = brandLogo;
    } else {
      avatarSrc = `https://unavatar.io/${encodeURIComponent(email)}?fallback=false`;
    }
  }

  if (avatarSrc && !loadError) {
    return (
      <img
        src={avatarSrc}
        alt={name || email || 'Avatar'}
        onError={() => setLoadError(true)}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          minWidth: `${size}px`,
          minHeight: `${size}px`,
          borderRadius: '50%',
          objectFit: 'cover',
          flexShrink: 0,
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          ...style
        }}
      />
    );
  }

  return (
    <div
      style={{
        width: `${size}px`,
        height: `${size}px`,
        minWidth: `${size}px`,
        minHeight: `${size}px`,
        borderRadius: '50%',
        backgroundColor: bgColor,
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: `${Math.max(11, Math.round(size * 0.44))}px`,
        flexShrink: 0,
        textTransform: 'uppercase',
        userSelect: 'none',
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
        ...style
      }}
    >
      {initial}
    </div>
  );
}

export default function App() {
  // Auth states
  const [token, setToken] = useState(localStorage.getItem('mail_token') || '');
  const [userEmail, setUserEmail] = useState(localStorage.getItem('mail_email') || '');
  const [isAdmin, setIsAdmin] = useState(localStorage.getItem('mail_is_admin') === 'true');
  const [portalMode, setPortalMode] = useState(window.PORTAL_MODE || 'webmail');

  // App Navigation ('mail', 'calendar', 'contacts', 'settings')
  const [activeApp, setActiveApp] = useState('mail');

  // Theme & Accent states
  const [theme, setTheme] = useState(localStorage.getItem('mail_theme') || 'light');
  const [accentColor, setAccentColor] = useState(localStorage.getItem('mail_accent') || 'blue');

  // UI state
  const [activeFolder, setActiveFolder] = useState('INBOX');
  const [folders, setFolders] = useState([]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [isNewFolderOpen, setIsNewFolderOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [quickFilter, setQuickFilter] = useState('all');
  const [toast, setToast] = useState(null);

  // Emails states
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedUids, setSelectedUids] = useState(new Set());
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);
  const [storageUsage, setStorageUsage] = useState({ usedMb: 0, totalMb: 1024, percent: 0 });

  // Labels state
  const [labels, setLabels] = useState([]);
  const [isLabelModalOpen, setIsLabelModalOpen] = useState(false);
  const [editingLabel, setEditingLabel] = useState(null);
  const [labelForm, setLabelForm] = useState({ name: '', color: '#6366f1' });
  const [isLabelPickerOpen, setIsLabelPickerOpen] = useState(false);
  const [labelSearchQuery, setLabelSearchQuery] = useState('');

  // Compose states
  const [composeState, setComposeState] = useState({
    isOpen: false,
    isMinimized: false,
    isMaximized: false,
    draftUid: null,
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    attachments: [],
    isSending: false,
    isSavingDraft: false,
    isEditingScheduled: false,
    scheduledId: null,
    scheduledFor: null
  });
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [isSchedulePopoverOpen, setIsSchedulePopoverOpen] = useState(false);
  const [customScheduleDate, setCustomScheduleDate] = useState('');
  const [customScheduleTime, setCustomScheduleTime] = useState('08:00');
  const [isContactPickerOpen, setIsContactPickerOpen] = useState(false);
  const [contactTargetField, setContactTargetField] = useState('to');

  // Scheduled emails state
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [isLoadingScheduled, setIsLoadingScheduled] = useState(false);


  // Calendar states
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [currentMonthDate, setCurrentMonthDate] = useState(new Date());
  const [isAddEventOpen, setIsAddEventOpen] = useState(false);
  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventDate, setNewEventDate] = useState(new Date().toISOString().split('T')[0]);
  const [newEventType, setNewEventType] = useState('event');

  // Contacts states
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [newContactName, setNewContactName] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');

  // Settings states & sub-tabs
  const [activeSettingsTab, setActiveSettingsTab] = useState('profile');
  const [profile, setProfile] = useState({
    displayName: '',
    avatarUrl: '',
    signature: ''
  });
  const [userSettings, setUserSettings] = useState({
    signature: '',
    autoresponder_enabled: false,
    autoresponder_subject: '',
    autoresponder_body: '',
    forward_to: '',
    mega_connected: false,
    mega_email: '',
    mega_storage_used: 0,
    mega_storage_total: 0
  });
  const [aliases, setAliases] = useState([]);
  const [appPasswords, setAppPasswords] = useState([]);
  const [newAppName, setNewAppName] = useState('');
  const [generatedAppPass, setGeneratedAppPass] = useState(null);
  const [currentPassInput, setCurrentPassInput] = useState('');
  const [newPassInput, setNewPassInput] = useState('');

  // Cloud MEGA states
  const [megaEmailInput, setMegaEmailInput] = useState('');
  const [megaPasswordInput, setMegaPasswordInput] = useState('');
  const [isLinkingMega, setIsLinkingMega] = useState(false);
  const [isSyncingMega, setIsSyncingMega] = useState(false);

  // Disposable Masked Email Aliases states
  const [maskedEmails, setMaskedEmails] = useState([]);
  const [isMaskModalOpen, setIsMaskModalOpen] = useState(false);
  const [editingMask, setEditingMask] = useState(null);
  const [maskForm, setMaskForm] = useState({
    label: '',
    prefix: '',
    domain: '',
    description: '',
    expiresInDays: 0
  });
  const [isGeneratingMask, setIsGeneratingMask] = useState(false);
  const [maskSearchQuery, setMaskSearchQuery] = useState('');
  const [maskFilterStatus, setMaskFilterStatus] = useState('all');
  const [copiedMaskId, setCopiedMaskId] = useState(null);

  // Login Form states
  const [loginMode, setLoginMode] = useState('workspace'); // 'workspace' or 'gmail'
  const [showGmailGuide, setShowGmailGuide] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [tempToken, setTempToken] = useState('');

  // Apply theme & accent
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.setAttribute('data-accent', accentColor);
    localStorage.setItem('mail_theme', theme);
    localStorage.setItem('mail_accent', accentColor);
  }, [theme, accentColor]);

  const showToast = (text, type = 'info') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Keyboard Shortcuts (Gmail style)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts if focus is in an input or textarea
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement.tagName)) return;
      if (composeState.isOpen) return;

      if (e.key === 'c' || e.key === 'C') {
        e.preventDefault();
        setComposeState(prev => ({ ...prev, isOpen: true, isMinimized: false }));
      } else if (e.key === 'r' || e.key === 'R') {
        if (selectedEmail) { e.preventDefault(); handleReply(false); }
      } else if (e.key === 'a' || e.key === 'A') {
        if (selectedEmail) { e.preventDefault(); handleReply(true); }
      } else if (e.key === 'f' || e.key === 'F') {
        if (selectedEmail) { e.preventDefault(); handleForward(); }
      } else if (e.key === 'u' || e.key === 'Escape') {
        if (selectedEmail) { e.preventDefault(); setSelectedEmail(null); }
      } else if (e.key === '#' || e.key === 'Delete') {
        if (selectedEmail || selectedUids.size > 0) { e.preventDefault(); deleteSelected(); }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEmail, selectedUids, composeState.isOpen]);

  // Initial load
  useEffect(() => {
    if (token) {
      fetchFolders();
      fetchLabels();
      fetchStorageUsage();
      fetchCalendar();
      fetchContacts();
      fetchSettings();
      fetchMaskedEmails();
      fetchScheduledEmails();
    }
  }, [token]);

  // Emails loader & folder switch
  useEffect(() => {
    if (token && portalMode !== 'admin' && activeApp === 'mail') {
      if (activeFolder === 'SCHEDULED') {
        fetchScheduledEmails();
      } else {
        fetchEmails();
      }
      setSelectedEmail(null);
      setSelectedUids(new Set());
    }
  }, [token, activeFolder, portalMode, activeApp]);

  const fetchLabels = async () => {
    try {
      const res = await fetch(`${API_BASE}/mail/labels`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) setLabels(data);
      }
    } catch (err) {
      console.error('Labels error:', err);
    }
  };

  const handleSaveLabel = async (e) => {
    if (e) e.preventDefault();
    if (!labelForm.name.trim()) {
      showToast('Label name is required', 'warning');
      return;
    }
    try {
      const isEdit = !!editingLabel?.id;
      const url = isEdit ? `${API_BASE}/mail/labels/${editingLabel.id}` : `${API_BASE}/mail/labels`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(labelForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save label');

      showToast(isEdit ? 'Label updated' : 'Label created', 'success');
      setIsLabelModalOpen(false);
      setEditingLabel(null);
      setLabelForm({ name: '', color: '#6366f1' });
      fetchLabels();
      fetchEmails();
    } catch (err) {
      showToast(err.message, 'danger');
    }
  };

  const handleDeleteLabel = async (labelId) => {
    if (!window.confirm('Are you sure you want to delete this label?')) return;
    try {
      const res = await fetch(`${API_BASE}/mail/labels/${labelId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        showToast('Label deleted', 'info');
        fetchLabels();
        if (activeFolder.startsWith('label:')) {
          setActiveFolder('INBOX');
        } else {
          fetchEmails();
        }
      }
    } catch (err) {
      showToast('Failed to delete label', 'danger');
    }
  };

  const handleToggleLabel = async (labelName) => {
    const targetUids = selectedEmail ? [selectedEmail.uid] : Array.from(selectedUids);
    if (targetUids.length === 0) return;

    const allHave = targetUids.every(uid => {
      const em = selectedEmail?.uid === uid ? selectedEmail : emails.find(e => e.uid === uid);
      return em?.labels?.includes(labelName);
    });

    try {
      if (allHave) {
        await fetch(`${API_BASE}/mail/emails/bulk-labels`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ folder: activeFolder, uids: targetUids, removeLabels: [labelName] })
        });
        setEmails(prev => prev.map(em => targetUids.includes(em.uid) ? { ...em, labels: (em.labels || []).filter(l => l !== labelName) } : em));
        if (selectedEmail && targetUids.includes(selectedEmail.uid)) {
          setSelectedEmail(prev => prev ? { ...prev, labels: (prev.labels || []).filter(l => l !== labelName) } : null);
        }
      } else {
        await fetch(`${API_BASE}/mail/emails/bulk-labels`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ folder: activeFolder, uids: targetUids, addLabels: [labelName] })
        });
        setEmails(prev => prev.map(em => {
          if (targetUids.includes(em.uid)) {
            const existing = new Set(em.labels || []);
            existing.add(labelName);
            return { ...em, labels: Array.from(existing) };
          }
          return em;
        }));
        if (selectedEmail && targetUids.includes(selectedEmail.uid)) {
          setSelectedEmail(prev => {
            if (!prev) return null;
            const existing = new Set(prev.labels || []);
            existing.add(labelName);
            return { ...prev, labels: Array.from(existing) };
          });
        }
      }
      fetchLabels();
    } catch (err) {
      console.error('Toggle label error:', err);
    }
  };

  const handleRemoveLabelFromEmail = async (uid, labelName) => {
    try {
      await fetch(`${API_BASE}/mail/emails/${uid}/labels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ folder: activeFolder, removeLabels: [labelName] })
      });
      setEmails(prev => prev.map(em => em.uid === uid ? { ...em, labels: (em.labels || []).filter(l => l !== labelName) } : em));
      if (selectedEmail && selectedEmail.uid === uid) {
        setSelectedEmail(prev => prev ? { ...prev, labels: (prev.labels || []).filter(l => l !== labelName) } : null);
      }
      fetchLabels();
    } catch (err) {
      console.error('Remove label error:', err);
    }
  };

  const fetchFolders = async () => {
    try {
      const res = await fetch(`${API_BASE}/mail/folders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401) return handleLogout();
      const data = await res.json();
      if (Array.isArray(data)) setFolders(data);
    } catch (err) {
      console.error('Folders error:', err);
    }
  };

  const fetchStorageUsage = async () => {
    try {
      const res = await fetch(`${API_BASE}/mail/storage-usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const usedMb = data.usedMb || (data.usedBytes ? (data.usedBytes / (1024 * 1024)).toFixed(2) : 0);
        const totalMb = data.quotaMb || 1024;
        const percent = Math.min(100, Math.round((usedMb / totalMb) * 100));
        setStorageUsage({ usedMb, totalMb, percent });
      }
    } catch (err) {
      console.error('Storage query error:', err);
    }
  };

  const fetchEmails = async (search = searchQuery) => {
    setIsLoadingEmails(true);
    try {
      let url = `${API_BASE}/mail/emails?folder=${encodeURIComponent(activeFolder)}&limit=50`;
      if (search.trim()) url += `&q=${encodeURIComponent(search.trim())}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) return handleLogout();
      const data = await res.json();
      setEmails(data.emails || []);
    } catch (err) {
      console.error('Emails fetch error:', err);
      showToast('Error loading emails', 'danger');
    } finally {
      setIsLoadingEmails(false);
    }
  };

  const fetchCalendar = async () => {
    try {
      const res = await fetch(`${API_BASE}/mail/calendar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCalendarEvents(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Calendar error:', err);
    }
  };

  const fetchContacts = async () => {
    try {
      const res = await fetch(`${API_BASE}/mail/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setContacts(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Contacts error:', err);
    }
  };

  const fetchSettings = async () => {
    try {
      const [resSet, resAliases, resApps, resPrefs] = await Promise.all([
        fetch(`${API_BASE}/mail/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/mail/aliases`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/mail/app-passwords`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/mail/preferences`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (resSet.ok) {
        const setJson = await resSet.json();
        setUserSettings(prev => ({ ...prev, ...setJson }));
      }
      if (resAliases.ok) {
        const alJson = await resAliases.json();
        setAliases(Array.isArray(alJson) ? alJson : []);
      }
      if (resApps.ok) {
        const appJson = await resApps.json();
        setAppPasswords(Array.isArray(appJson) ? appJson : []);
      }
      if (resPrefs && resPrefs.ok) {
        const pJson = await resPrefs.json();
        if (pJson.preferences && typeof pJson.preferences === 'object') {
          setProfile(prev => ({ ...prev, ...pJson.preferences }));
        }
      }
      const resMasked = await fetch(`${API_BASE}/mail/masked-emails`, { headers: { Authorization: `Bearer ${token}` } });
      if (resMasked.ok) {
        const mJson = await resMasked.json();
        setMaskedEmails(Array.isArray(mJson) ? mJson : []);
      }
    } catch (err) {
      console.error('Settings error:', err);
    }
  };

  const fetchMaskedEmails = async () => {
    try {
      const res = await fetch(`${API_BASE}/mail/masked-emails`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMaskedEmails(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Fetch masked emails error:', err);
    }
  };

  const handleCreateMaskedEmail = async (e) => {
    e.preventDefault();
    if (!maskForm.label.trim()) {
      showToast('Service / Label name is required', 'warning');
      return;
    }
    setIsGeneratingMask(true);
    try {
      const res = await fetch(`${API_BASE}/mail/masked-emails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(maskForm)
      });
      const data = await res.json();
      if (res.ok) {
        showToast('Disposable masked email created!', 'success');
        setIsMaskModalOpen(false);
        setMaskForm({ label: '', prefix: '', domain: '', description: '', expiresInDays: 0 });
        fetchMaskedEmails();
      } else {
        showToast(data.error || 'Failed to generate masked email', 'danger');
      }
    } catch (err) {
      showToast('Failed to generate masked email', 'danger');
    } finally {
      setIsGeneratingMask(false);
    }
  };

  const handleToggleMaskedEmail = async (maskId) => {
    try {
      const res = await fetch(`${API_BASE}/mail/masked-emails/${maskId}/toggle`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setMaskedEmails(prev => prev.map(m => m.id === maskId ? { ...m, is_active: data.is_active } : m));
        showToast(data.is_active ? 'Mask activated (forwarding on)' : 'Mask paused (blocking incoming spam)', 'info');
      } else {
        showToast(data.error || 'Failed to toggle mask status', 'danger');
      }
    } catch (err) {
      showToast('Failed to toggle mask status', 'danger');
    }
  };

  const handleDeleteMaskedEmail = async (maskId, aliasEmail) => {
    if (!window.confirm(`Permanently delete masked alias "${aliasEmail}"?\nAll future emails sent to this address will be rejected immediately.`)) return;
    try {
      const res = await fetch(`${API_BASE}/mail/masked-emails/${maskId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setMaskedEmails(prev => prev.filter(m => m.id !== maskId));
        showToast('Masked email permanently deleted', 'info');
      } else {
        showToast('Failed to delete masked email', 'danger');
      }
    } catch (err) {
      showToast('Failed to delete masked email', 'danger');
    }
  };

  const handleCopyMask = (aliasEmail, id) => {
    navigator.clipboard.writeText(aliasEmail);
    setCopiedMaskId(id);
    showToast(`Copied ${aliasEmail} to clipboard!`, 'success');
    setTimeout(() => setCopiedMaskId(null), 2500);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoggingIn(true);
    try {
      const isGmailEmail = loginEmail.toLowerCase().endsWith('@gmail.com') || loginEmail.toLowerCase().endsWith('@googlemail.com');
      let cleanPass = loginPassword.trim();
      if (isGmailEmail || loginMode === 'gmail') {
        cleanPass = cleanPass.replace(/\s+/g, '');
      }

      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail.trim(), password: cleanPass })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');

      if (data.twoFactorRequired) {
        setTwoFactorRequired(true);
        setTempToken(data.tempToken);
        setIsLoggingIn(false);
        return;
      }

      setToken(data.token);
      setUserEmail(data.email);
      setIsAdmin(!!data.isAdmin);
      localStorage.setItem('mail_token', data.token);
      localStorage.setItem('mail_email', data.email);
      localStorage.setItem('mail_is_admin', data.isAdmin ? 'true' : 'false');
      showToast(`Signed in as ${data.email}`, 'success');
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handle2faVerify = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoggingIn(true);
    try {
      const res = await fetch(`${API_BASE}/auth/2fa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tempToken, code: twoFactorCode })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Invalid 2FA code');

      setToken(data.token);
      setUserEmail(data.email);
      setIsAdmin(!!data.isAdmin);
      localStorage.setItem('mail_token', data.token);
      localStorage.setItem('mail_email', data.email);
      localStorage.setItem('mail_is_admin', data.isAdmin ? 'true' : 'false');
      setTwoFactorRequired(false);
      showToast(`Welcome back, ${data.email}`, 'success');
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    setToken('');
    setUserEmail('');
    setIsAdmin(false);
    localStorage.removeItem('mail_token');
    localStorage.removeItem('mail_email');
    localStorage.removeItem('mail_is_admin');
    setIsAccountMenuOpen(false);
    showToast('Signed out', 'info');
  };

  const openEmail = async (email) => {
    const targetFolder = (email?.folder && !email.folder.startsWith('label:') && email.folder !== 'STARRED')
      ? email.folder
      : (activeFolder.startsWith('label:') || activeFolder === 'STARRED' ? (email?.folder || 'INBOX') : activeFolder);

    // If opening an email from Drafts folder, open directly in Compose mode to edit & send!
    if (activeFolder.includes('Drafts') || targetFolder.includes('Drafts')) {
      let bodyContent = email.bodyText || email.bodyHtml || email.snippet || '';
      let atts = email.attachments || [];
      if (!email.bodyHtml && !email.bodyText) {
        try {
          const res = await fetch(`${API_BASE}/mail/emails/${email.uid}?folder=${encodeURIComponent(targetFolder)}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const detail = await res.json();
            bodyContent = detail.bodyText || detail.bodyHtml || '';
            atts = detail.attachments || [];
          }
        } catch (err) {
          console.error('Detail fetch error:', err);
        }
      }
      setComposeState({
        isOpen: true,
        isMinimized: false,
        isMaximized: false,
        draftUid: email.uid,
        to: (email.to || []).map(t => t.address || t).join(', '),
        cc: (email.cc || []).map(c => c.address || c).join(', '),
        bcc: '',
        subject: email.subject || '',
        body: bodyContent,
        attachments: atts,
        isSending: false,
        isSavingDraft: false
      });
      return;
    }

    setSelectedEmail(email);
    if (!email.isRead) {
      try {
        await fetch(`${API_BASE}/mail/emails/${email.uid}/read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ folder: targetFolder, isRead: true })
        });
        setEmails(prev => prev.map(e => (e.uid === email.uid && (e.folder || 'INBOX') === (email.folder || 'INBOX')) ? { ...e, isRead: true } : e));
      } catch (err) {
        console.error('Mark read error:', err);
      }
    }

    if (!email.bodyHtml && !email.bodyText) {
      try {
        const res = await fetch(`${API_BASE}/mail/emails/${email.uid}?folder=${encodeURIComponent(targetFolder)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const detail = await res.json();
          setSelectedEmail(prev => prev && prev.uid === email.uid && (prev.folder || 'INBOX') === (email.folder || 'INBOX') ? { ...prev, ...detail } : prev);
        }
      } catch (err) {
        console.error('Detail fetch error:', err);
      }
    }
  };

  const toggleStar = async (e, email) => {
    if (e) e.stopPropagation();
    const newStarred = !email.isStarred;
    const targetFolder = (email?.folder && !email.folder.startsWith('label:') && email.folder !== 'STARRED')
      ? email.folder
      : (activeFolder.startsWith('label:') || activeFolder === 'STARRED' ? (email?.folder || 'INBOX') : activeFolder);

    setEmails(prev => prev.map(item => (item.uid === email.uid && (item.folder || 'INBOX') === (email.folder || 'INBOX')) ? { ...item, isStarred: newStarred } : item));
    if (selectedEmail && selectedEmail.uid === email.uid && (selectedEmail.folder || 'INBOX') === (email.folder || 'INBOX')) {
      setSelectedEmail(prev => ({ ...prev, isStarred: newStarred }));
    }

    try {
      const res = await fetch(`${API_BASE}/mail/emails/${email.uid}/star`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ folder: targetFolder, star: newStarred, starred: newStarred })
      });
      if (res.ok) {
        showToast(newStarred ? 'Conversation starred' : 'Conversation unstarred', 'info');
      }
    } catch (err) {
      console.error('Star error:', err);
    }
  };

  const toggleSelectUid = (e, uid) => {
    e.stopPropagation();
    setSelectedUids(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUids.size === filteredEmails.length) {
      setSelectedUids(new Set());
    } else {
      setSelectedUids(new Set(filteredEmails.map(e => e.uid)));
    }
  };

  const deleteSingleEmail = async (uid, folder) => {
    const emailObj = emails.find(e => e.uid === uid);
    const emailFolder = (folder && !folder.startsWith('label:')) 
      ? folder 
      : (emailObj?.folder && !emailObj.folder.startsWith('label:'))
        ? emailObj.folder 
        : (activeFolder.startsWith('label:') ? 'INBOX' : activeFolder);

    const isTrashFolder = emailFolder.includes('Deleted') || emailFolder.includes('Trash') || activeFolder.includes('Deleted') || activeFolder.includes('Trash');

    // Optimistic UI update
    setEmails(prev => prev.filter(e => e.uid !== uid));
    if (selectedEmail && selectedEmail.uid === uid) setSelectedEmail(null);
    setSelectedUids(prev => {
      const next = new Set(prev);
      next.delete(uid);
      return next;
    });

    showToast(isTrashFolder ? '1 email permanently deleted' : '1 conversation moved to Trash', 'info');

    try {
      await fetch(`${API_BASE}/mail/emails/${uid}?folder=${encodeURIComponent(emailFolder)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Delete single error:', err);
    }
  };

  const deleteSelected = async () => {
    const uidsToDelete = selectedEmail ? [selectedEmail.uid] : Array.from(selectedUids);
    if (uidsToDelete.length === 0) return;

    const emailObjs = emails.filter(e => uidsToDelete.includes(e.uid));
    setEmails(prev => prev.filter(e => !uidsToDelete.includes(e.uid)));
    setSelectedUids(new Set());
    if (selectedEmail) setSelectedEmail(null);

    const isTrashFolder = activeFolder.includes('Deleted') || activeFolder.includes('Trash');
    showToast(isTrashFolder ? `${uidsToDelete.length} email(s) permanently deleted` : `${uidsToDelete.length} conversation(s) moved to Trash`, 'info');

    for (const uid of uidsToDelete) {
      const emailObj = emailObjs.find(e => e.uid === uid);
      const emailFolder = (emailObj && emailObj.folder && !emailObj.folder.startsWith('label:')) 
        ? emailObj.folder 
        : (activeFolder.startsWith('label:') ? 'INBOX' : activeFolder);

      try {
        await fetch(`${API_BASE}/mail/emails/${uid}?folder=${encodeURIComponent(emailFolder)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        console.error('Delete error:', err);
      }
    }
  };

  const handleSaveDraft = async () => {
    if (!composeState.to.trim() && !composeState.subject.trim() && !composeState.body.trim()) {
      showToast('Draft is empty', 'warning');
      return;
    }
    setComposeState(prev => ({ ...prev, isSavingDraft: true }));
    try {
      const res = await fetch(`${API_BASE}/mail/drafts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          to: composeState.to,
          cc: composeState.cc,
          bcc: composeState.bcc,
          subject: composeState.subject || '(Draft)',
          body: composeState.body,
          draftUid: composeState.draftUid,
          attachments: composeState.attachments
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save draft');

      setComposeState(prev => ({ ...prev, draftUid: data.uid, isSavingDraft: false }));
      showToast('Draft saved to Drafts', 'success');
      if (activeFolder.includes('Drafts')) {
        fetchEmails();
      }
      fetchFolders();
    } catch (err) {
      showToast(err.message, 'danger');
      setComposeState(prev => ({ ...prev, isSavingDraft: false }));
    }
  };

  const handleSendEmail = async () => {
    if (!composeState.to.trim()) {
      showToast('Please specify at least one recipient', 'warning');
      return;
    }
    setComposeState(prev => ({ ...prev, isSending: true }));
    try {
      let finalBody = composeState.body;
      if (userSettings.signature && !finalBody.includes(userSettings.signature)) {
        finalBody = `${finalBody}\n\n--\n${userSettings.signature}`;
      }

      const res = await fetch(`${API_BASE}/mail/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          to: composeState.to,
          cc: composeState.cc,
          bcc: composeState.bcc,
          subject: composeState.subject || '(No Subject)',
          body: finalBody,
          bodyHtml: finalBody.replace(/\n/g, '<br/>'),
          isHtml: false,
          draftUid: composeState.draftUid,
          attachments: composeState.attachments
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send message');

      showToast('Message sent successfully', 'success');
      setComposeState({
        isOpen: false,
        isMinimized: false,
        isMaximized: false,
        to: '',
        cc: '',
        bcc: '',
        subject: '',
        body: '',
        attachments: [],
        isSending: false,
        draftUid: null,
        isSavingDraft: false
      });

      // Refresh emails and folder unread/total badges
      fetchEmails();
      fetchFolders();
    } catch (err) {
      showToast(err.message, 'danger');
      setComposeState(prev => ({ ...prev, isSending: false }));
    }
  };

  // ================= SCHEDULE SEND HANDLERS =================

  const handleScheduleEmail = async (scheduledFor) => {
    if (!composeState.to.trim()) {
      showToast('Please specify at least one recipient', 'warning');
      return;
    }
    setComposeState(prev => ({ ...prev, isSending: true }));
    setIsSchedulePopoverOpen(false);
    try {
      let finalBody = composeState.body;
      if (userSettings.signature && !finalBody.includes(userSettings.signature)) {
        finalBody = `${finalBody}\n\n--\n${userSettings.signature}`;
      }

      const res = await fetch(`${API_BASE}/mail/scheduled`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          to: composeState.to,
          cc: composeState.cc,
          bcc: composeState.bcc,
          subject: composeState.subject || '(No Subject)',
          body: finalBody,
          bodyHtml: finalBody.replace(/\n/g, '<br/>'),
          attachments: composeState.attachments,
          scheduledFor: scheduledFor
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to schedule message');

      showToast(data.message || 'Email scheduled successfully', 'success');
      setComposeState({
        isOpen: false,
        isMinimized: false,
        isMaximized: false,
        to: '',
        cc: '',
        bcc: '',
        subject: '',
        body: '',
        attachments: [],
        isSending: false,
        draftUid: null,
        isSavingDraft: false
      });
      fetchScheduledEmails();
    } catch (err) {
      showToast(err.message, 'danger');
      setComposeState(prev => ({ ...prev, isSending: false }));
    }
  };

  const handleOpenScheduledEmail = (item) => {
    setComposeState({
      isOpen: true,
      isMinimized: false,
      isMaximized: false,
      to: item.to_recipients || '',
      cc: item.cc_recipients || '',
      bcc: item.bcc_recipients || '',
      subject: item.subject || '',
      body: item.body_plain || (item.body_html ? item.body_html.replace(/<br\s*[\/]?>/gi, '\n') : ''),
      attachments: item.attachments || [],
      isSending: false,
      draftUid: null,
      isSavingDraft: false,
      isEditingScheduled: true,
      scheduledId: item.id,
      scheduledFor: item.scheduled_for
    });
  };

  const handleUpdateScheduledEmail = async (overrideTime) => {
    if (!composeState.to.trim()) {
      showToast('Please specify at least one recipient', 'warning');
      return;
    }
    setComposeState(prev => ({ ...prev, isSending: true }));
    setIsSchedulePopoverOpen(false);
    try {
      let finalBody = composeState.body;
      const scheduledTime = overrideTime || composeState.scheduledFor || new Date(Date.now() + 3600000).toISOString();

      const res = await fetch(`${API_BASE}/mail/scheduled/${composeState.scheduledId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          to: composeState.to,
          cc: composeState.cc,
          bcc: composeState.bcc,
          subject: composeState.subject || '(No Subject)',
          body: finalBody,
          bodyHtml: finalBody.replace(/\n/g, '<br/>'),
          attachments: composeState.attachments,
          scheduledFor: scheduledTime
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update scheduled email');

      showToast(data.message || 'Scheduled email updated successfully', 'success');
      setComposeState({
        isOpen: false,
        isMinimized: false,
        isMaximized: false,
        to: '',
        cc: '',
        bcc: '',
        subject: '',
        body: '',
        attachments: [],
        isSending: false,
        draftUid: null,
        isSavingDraft: false,
        isEditingScheduled: false,
        scheduledId: null,
        scheduledFor: null
      });
      fetchScheduledEmails();
    } catch (err) {
      showToast(err.message, 'danger');
      setComposeState(prev => ({ ...prev, isSending: false }));
    }
  };

  const fetchScheduledEmails = async () => {
    if (!token) return;
    setIsLoadingScheduled(true);
    try {
      const res = await fetch(`${API_BASE}/mail/scheduled`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setScheduledEmails(data || []);
      }
    } catch (err) {
      console.error('Error fetching scheduled emails:', err);
    } finally {
      setIsLoadingScheduled(false);
    }
  };

  const handleCancelScheduledEmail = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/mail/scheduled/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        showToast('Scheduled email cancelled', 'info');
        fetchScheduledEmails();
      }
    } catch (err) {
      showToast(err.message, 'danger');
    }
  };

  const handleSendScheduledNow = async (id) => {
    try {
      showToast('Dispatching scheduled email...', 'info');
      const res = await fetch(`${API_BASE}/mail/scheduled/${id}/send-now`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        showToast('Scheduled email sent now', 'success');
        fetchScheduledEmails();
        fetchEmails();
      }
    } catch (err) {
      showToast(err.message, 'danger');
    }
  };

  // ================= ONE-CLICK FOLDER EXPORT HANDLERS =================

  const handleExportFolder = async (folderName = activeFolder) => {
    try {
      showToast(`Preparing export for ${folderName}...`, 'info');
      const res = await fetch(`${API_BASE}/mail/folders/export?folder=${encodeURIComponent(folderName)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to export folder');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Export_${folderName.replace(/[^a-zA-Z0-9_-]/g, '_')}_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showToast('Folder exported successfully (.zip)', 'success');
    } catch (err) {
      showToast(err.message, 'danger');
    }
  };

  const handleExportSelectedEmails = async () => {
    if (selectedUids.size === 0) return;
    try {
      showToast(`Exporting ${selectedUids.size} email(s)...`, 'info');
      const res = await fetch(`${API_BASE}/mail/emails/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          folder: activeFolder,
          uids: Array.from(selectedUids)
        })
      });
      if (!res.ok) throw new Error('Failed to export selected emails');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Selected_Emails_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showToast('Selected emails exported successfully (.zip)', 'success');
    } catch (err) {
      showToast(err.message, 'danger');
    }
  };


  const handleFileAttach = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result.split(',')[1];
        setComposeState(prev => ({
          ...prev,
          attachments: [
            ...prev.attachments,
            { filename: file.name, content: base64, size: file.size, contentType: file.type }
          ]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleReply = (isAll = false) => {
    if (!selectedEmail) return;
    const to = selectedEmail.from?.address || '';
    const subject = selectedEmail.subject?.startsWith('Re:') ? selectedEmail.subject : `Re: ${selectedEmail.subject}`;
    const quoted = `\n\n\n--- On ${new Date(selectedEmail.date).toLocaleString()}, ${selectedEmail.from?.name || to} wrote:\n> ${(selectedEmail.snippet || '').replace(/\n/g, '\n> ')}`;
    setComposeState({
      isOpen: true,
      isMinimized: false,
      isMaximized: false,
      to,
      cc: isAll ? (selectedEmail.to || []).map(t => t.address).filter(a => a !== userEmail).join(', ') : '',
      bcc: '',
      subject,
      body: quoted,
      attachments: [],
      isSending: false
    });
  };

  const handleForward = () => {
    if (!selectedEmail) return;
    const subject = selectedEmail.subject?.startsWith('Fwd:') ? selectedEmail.subject : `Fwd: ${selectedEmail.subject}`;
    const quoted = `\n\n\n---------- Forwarded message ---------\nFrom: ${selectedEmail.from?.name || ''} <${selectedEmail.from?.address || ''}>\nDate: ${new Date(selectedEmail.date).toLocaleString()}\nSubject: ${selectedEmail.subject}\n\n${selectedEmail.snippet || ''}`;
    setComposeState({
      isOpen: true,
      isMinimized: false,
      isMaximized: false,
      to: '',
      cc: '',
      bcc: '',
      subject,
      body: quoted,
      attachments: selectedEmail.attachments || [],
      isSending: false
    });
  };

  const handlePrintEmail = () => {
    window.print();
  };

  const handleDownloadEml = () => {
    if (!selectedEmail) return;
    const rawContent = `From: ${selectedEmail.from?.name || ''} <${selectedEmail.from?.address || ''}>\nTo: ${selectedEmail.to?.map(t => t.address).join(', ')}\nDate: ${new Date(selectedEmail.date).toUTCString()}\nSubject: ${selectedEmail.subject}\n\n${selectedEmail.bodyText || selectedEmail.snippet || ''}`;
    const blob = new Blob([rawContent], { type: 'message/rfc822' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedEmail.subject || 'message'}.eml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Calendar Helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDayOfWeek = firstDay.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < startDayOfWeek; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d));
    return days;
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    if (!newEventTitle.trim()) return;
    try {
      const res = await fetch(`${API_BASE}/mail/calendar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: newEventTitle,
          description: newEventType === 'reminder' ? '#reminder' : (newEventType === 'birthday' ? '#birthday' : ''),
          start_time: new Date(newEventDate).toISOString(),
          end_time: new Date(newEventDate).toISOString(),
          event_type: newEventType,
          is_all_day: true
        })
      });
      if (res.ok) {
        showToast('Event scheduled', 'success');
        setIsAddEventOpen(false);
        setNewEventTitle('');
        fetchCalendar();
      }
    } catch (err) {
      showToast('Failed to schedule event', 'danger');
    }
  };

  const handleDeleteEvent = async (id) => {
    try {
      await fetch(`${API_BASE}/mail/calendar/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarEvents(prev => prev.filter(e => e.id !== id));
      showToast('Event removed');
    } catch (err) {
      console.error(err);
    }
  };

  // Contacts Helpers
  const handleAddContact = async (e) => {
    e.preventDefault();
    if (!newContactEmail.trim()) return;
    try {
      const res = await fetch(`${API_BASE}/mail/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: newContactName || newContactEmail.split('@')[0],
          email: newContactEmail,
          phone: newContactPhone,
          is_favorite: false
        })
      });
      if (res.ok) {
        showToast('Contact saved', 'success');
        setIsAddContactOpen(false);
        setNewContactName('');
        setNewContactEmail('');
        setNewContactPhone('');
        fetchContacts();
      }
    } catch (err) {
      showToast('Failed to add contact', 'danger');
    }
  };

  const handleDeleteContact = async (id) => {
    try {
      await fetch(`${API_BASE}/mail/contacts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(prev => prev.filter(c => c.id !== id));
      showToast('Contact removed');
    } catch (err) {
      console.error(err);
    }
  };

  // Settings & Integrations Helpers
  const handleSaveSettings = async (e) => {
    if (e) e.preventDefault();
    try {
      const res = await fetch(`${API_BASE}/mail/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(userSettings)
      });
      if (res.ok) showToast('Settings saved successfully', 'success');
    } catch (err) {
      showToast('Failed to save settings', 'danger');
    }
  };

  const handleSaveProfile = async (e) => {
    if (e) e.preventDefault();
    try {
      const res = await fetch(`${API_BASE}/mail/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(profile)
      });
      if (res.ok) {
        showToast('Account profile & avatar updated successfully!', 'success');
      } else {
        showToast('Failed to save profile', 'danger');
      }
    } catch (err) {
      showToast('Error saving profile', 'danger');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword: currentPassInput, newPassword: newPassInput })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Password update failed');
      showToast('Password changed successfully!', 'success');
      setCurrentPassInput('');
      setNewPassInput('');
    } catch (err) {
      showToast(err.message, 'danger');
    }
  };

  const handleCreateAppPassword = async (e) => {
    e.preventDefault();
    if (!newAppName.trim()) return;
    try {
      const res = await fetch(`${API_BASE}/mail/app-passwords`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: newAppName.trim() })
      });
      const data = await res.json();
      if (res.ok && data.appPassword) {
        setGeneratedAppPass({ name: newAppName, password: data.appPassword });
        setNewAppName('');
        fetchSettings();
        showToast('App Password generated!', 'success');
      } else {
        showToast(data.error || 'Failed to generate app password', 'danger');
      }
    } catch (err) {
      showToast('Error generating app password', 'danger');
    }
  };

  const handleDeleteAppPassword = async (id) => {
    try {
      await fetch(`${API_BASE}/mail/app-passwords/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppPasswords(prev => prev.filter(p => p.id !== id));
      showToast('App Password revoked');
    } catch (err) {
      console.error(err);
    }
  };

  const handleConnectMega = async (e) => {
    e.preventDefault();
    if (!megaEmailInput || !megaPasswordInput) return;
    setIsLinkingMega(true);
    try {
      const res = await fetch(`${API_BASE}/mail/link-mega`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          megaEmail: megaEmailInput,
          megaPassword: megaPasswordInput,
          email: megaEmailInput,
          password: megaPasswordInput
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showToast('MEGA Cloud Drive connected successfully!', 'success');
        setMegaPasswordInput('');
        fetchSettings();
        fetchStorageUsage();
      } else {
        showToast(data.error || 'Invalid MEGA credentials', 'danger');
      }
    } catch (err) {
      showToast('Failed to connect MEGA storage', 'danger');
    } finally {
      setIsLinkingMega(false);
    }
  };

  const handleUnlinkMega = async () => {
    try {
      const res = await fetch(`${API_BASE}/mail/unlink-mega`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        showToast('MEGA Cloud Drive unlinked');
        fetchSettings();
        fetchStorageUsage();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSyncMega = async () => {
    try {
      setIsSyncingMega(true);
      showToast('Synchronizing with MEGA Cloud...', 'info');
      const res = await fetch(`${API_BASE}/mail/sync-mega`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showToast('MEGA Cloud storage synchronized!', 'success');
        fetchSettings();
        fetchStorageUsage();
      } else {
        showToast(data.error || 'Failed to synchronize with MEGA', 'danger');
      }
    } catch (err) {
      console.error(err);
      showToast('Failed to synchronize with MEGA', 'danger');
    } finally {
      setIsSyncingMega(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    if (date.getFullYear() === now.getFullYear()) {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
    return date.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Filtered emails based on quick chips
  const filteredEmails = emails.filter(e => {
    if (quickFilter === 'unread') return !e.isRead;
    if (quickFilter === 'starred') return e.isStarred;
    if (quickFilter === 'attachments') return e.hasAttachments;
    return true;
  });

  // Login View
  if (!token) {
    const isGmailInput = loginEmail.toLowerCase().endsWith('@gmail.com') || loginEmail.toLowerCase().endsWith('@googlemail.com') || loginMode === 'gmail';

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: 'var(--bg-app)',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: 'var(--bg-surface)',
          padding: '36px',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-md)',
          width: '100%',
          maxWidth: '460px',
          border: '1px solid var(--border-color)',
          transition: 'all 0.2s ease'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: isGmailInput ? 'rgba(66, 133, 244, 0.12)' : 'var(--google-blue-light)',
              color: isGmailInput ? 'var(--google-blue)' : 'var(--google-blue)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '14px',
              transition: 'all 0.2s ease'
            }}>
              {isGmailInput ? (
                <svg width="28" height="28" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                </svg>
              ) : (
                <Mail size={30} />
              )}
            </div>
            <h1 style={{ fontSize: '22px', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
              {isGmailInput ? 'Sign in with Gmail' : 'Sign in to Webmail'}
            </h1>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '6px', marginBottom: 0 }}>
              {isGmailInput
                ? 'Access your Google Mail directly in Mainnode Webmail'
                : 'Webmail, Calendar, Contacts & Workspace'}
            </p>
          </div>

          {/* Mode Switcher Tabs */}
          <div style={{
            display: 'flex',
            backgroundColor: 'var(--bg-app)',
            borderRadius: '10px',
            padding: '4px',
            marginBottom: '20px',
            border: '1px solid var(--border-color)'
          }}>
            <button
              type="button"
              onClick={() => {
                setLoginMode('workspace');
                if (loginEmail.endsWith('@gmail.com') || loginEmail.endsWith('@googlemail.com')) {
                  setLoginEmail('');
                }
              }}
              style={{
                flex: 1,
                padding: '8px 12px',
                fontSize: '13px',
                fontWeight: loginMode === 'workspace' ? 600 : 400,
                color: loginMode === 'workspace' ? 'var(--text-primary)' : 'var(--text-muted)',
                backgroundColor: loginMode === 'workspace' ? 'var(--bg-surface)' : 'transparent',
                border: 'none',
                borderRadius: '7px',
                cursor: 'pointer',
                boxShadow: loginMode === 'workspace' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.15s ease'
              }}
            >
              <Mail size={15} />
              <span>Workspace</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setLoginMode('gmail');
                if (!loginEmail.endsWith('@gmail.com')) {
                  setLoginEmail('');
                }
              }}
              style={{
                flex: 1,
                padding: '8px 12px',
                fontSize: '13px',
                fontWeight: loginMode === 'gmail' ? 600 : 400,
                color: loginMode === 'gmail' ? 'var(--google-blue)' : 'var(--text-muted)',
                backgroundColor: loginMode === 'gmail' ? 'var(--bg-surface)' : 'transparent',
                border: 'none',
                borderRadius: '7px',
                cursor: 'pointer',
                boxShadow: loginMode === 'gmail' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.15s ease'
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
              </svg>
              <span>Gmail / Google</span>
            </button>
          </div>

          {loginError && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              color: 'var(--google-red)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '13px',
              marginBottom: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500 }}>
                <AlertCircle size={16} style={{ flexShrink: 0 }} />
                <span>{loginError}</span>
              </div>
              {isGmailInput && (
                <div style={{ fontSize: '12px', marginTop: '4px', borderTop: '1px dashed rgba(239,68,68,0.3)', paddingTop: '6px' }}>
                  Need help? Generate a 16-character App Password at{' '}
                  <a
                    href="https://myaccount.google.com/apppasswords"
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: 'var(--google-blue)', textDecoration: 'underline', fontWeight: 600 }}
                  >
                    myaccount.google.com/apppasswords ↗
                  </a>
                </div>
              )}
            </div>
          )}

          {!twoFactorRequired ? (
            <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="gmail-input-group">
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{isGmailInput ? 'Gmail Address' : 'Email Address'}</span>
                  {isGmailInput && (
                    <span style={{ fontSize: '11px', color: 'var(--google-blue)', fontWeight: 500 }}>Google IMAP</span>
                  )}
                </label>
                <input
                  type="email"
                  className="gmail-form-input"
                  placeholder={isGmailInput ? 'yourname@gmail.com' : 'admin@mainnode.qzz.io'}
                  value={loginEmail}
                  onChange={e => {
                    setLoginEmail(e.target.value);
                    if (e.target.value.toLowerCase().includes('@gmail.com') || e.target.value.toLowerCase().includes('@googlemail.com')) {
                      setLoginMode('gmail');
                    }
                  }}
                  required
                  autoFocus
                />
              </div>

              <div className="gmail-input-group">
                <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{isGmailInput ? 'Google App Password' : 'Password'}</span>
                  {isGmailInput && (
                    <button
                      type="button"
                      onClick={() => setShowGmailGuide(!showGmailGuide)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--google-blue)',
                        fontSize: '11px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3px',
                        padding: 0
                      }}
                    >
                      <HelpCircle size={12} />
                      <span>{showGmailGuide ? 'Hide Guide' : 'How to get App Password?'}</span>
                    </button>
                  )}
                </label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="gmail-form-input"
                    style={{ width: '100%', paddingRight: '40px', letterSpacing: isGmailInput ? '1px' : 'normal' }}
                    placeholder={isGmailInput ? 'abcd efgh ijkl mnop' : '••••••••••••'}
                    value={loginPassword}
                    onChange={e => setLoginPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ position: 'absolute', right: '10px', color: 'var(--text-muted)', padding: '4px', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Gmail Guide Collapsible Accordion */}
              {isGmailInput && (showGmailGuide || loginMode === 'gmail') && (
                <div style={{
                  backgroundColor: 'var(--bg-app)',
                  borderRadius: '8px',
                  padding: '12px 14px',
                  border: '1px solid var(--border-color)',
                  fontSize: '12px',
                  lineHeight: '1.5'
                }}>
                  <div style={{ fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                    <Info size={14} color="var(--google-blue)" />
                    <span>How to sign in with your Gmail account:</span>
                  </div>
                  <ol style={{ margin: '0 0 10px 0', paddingLeft: '18px', color: 'var(--text-secondary)' }}>
                    <li>Ensure <strong>2-Step Verification</strong> is ON in your Google Account.</li>
                    <li>Go to <strong>Google App Passwords</strong>.</li>
                    <li>Create an app named <em>Webmail</em> and copy the 16-letter code.</li>
                    <li>Paste the code in the password box above (spaces are OK).</li>
                  </ol>
                  <a
                    href="https://myaccount.google.com/apppasswords"
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      color: '#ffffff',
                      backgroundColor: 'var(--google-blue)',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: 500,
                      textDecoration: 'none'
                    }}
                  >
                    <span>Generate Gmail App Password</span>
                    <ExternalLink size={12} />
                  </a>
                </div>
              )}

              <button
                type="submit"
                className="gmail-send-btn"
                style={{ justifyContent: 'center', height: '44px', marginTop: '4px' }}
                disabled={isLoggingIn}
              >
                {isLoggingIn ? 'Signing in...' : isGmailInput ? 'Sign In with Gmail' : 'Sign In'}
              </button>
            </form>
          ) : (
            <form onSubmit={handle2faVerify} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                Enter the 6-digit verification code from your authenticator app.
              </p>
              <input
                type="text"
                className="gmail-form-input"
                placeholder="000000"
                maxLength={6}
                value={twoFactorCode}
                onChange={e => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                required
                autoFocus
                style={{ textAlign: 'center', fontSize: '20px', letterSpacing: '4px' }}
              />
              <button
                type="submit"
                className="gmail-send-btn"
                style={{ justifyContent: 'center', height: '44px' }}
                disabled={isLoggingIn}
              >
                {isLoggingIn ? 'Verifying...' : 'Verify Code'}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Admin Console View
  if (portalMode === 'admin') {
    return (
      <Suspense fallback={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '12px' }}>
          <RefreshCw className="spin" size={24} color="var(--google-blue)" />
          <span>Loading Admin Console...</span>
        </div>
      }>
        <LazyAdminDashboard
          token={token}
          userEmail={userEmail}
          isAdmin={isAdmin}
          theme={theme}
          setTheme={setTheme}
          handleLogout={handleLogout}
          API_BASE={API_BASE}
          showAlert={showToast}
          switchToWebmail={() => setPortalMode('webmail')}
        />
      </Suspense>
    );
  }

  const unreadCount = emails.filter(e => !e.isRead).length;

  return (
    <div className="gmail-layout">
      {/* 1. TOP HEADER */}
      <header className="gmail-header">
        <div className="gmail-header-left">
          <button
            className="gmail-icon-btn"
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            title="Toggle sidebar"
          >
            <Menu size={20} />
          </button>
          <div className="gmail-brand" onClick={() => { setActiveApp('mail'); setActiveFolder('INBOX'); setSelectedEmail(null); }}>
            <Mail size={28} color="var(--google-blue)" />
            <span>MainNode Mail</span>
          </div>
        </div>

        {/* Center Search Bar */}
        <div className="gmail-search-container">
          <div className={`gmail-search-box ${isSearchFocused ? 'focused' : ''}`}>
            <Search size={20} color="var(--text-muted)" />
            <input
              type="text"
              className="gmail-search-input"
              placeholder={activeApp === 'mail' ? 'Search in mail (e.g. from:admin, has:attachment)' : (activeApp === 'contacts' ? 'Search contacts' : 'Search workspace')}
              value={activeApp === 'contacts' ? contactSearch : searchQuery}
              onChange={e => {
                if (activeApp === 'contacts') setContactSearch(e.target.value);
                else setSearchQuery(e.target.value);
              }}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              onKeyDown={e => e.key === 'Enter' && activeApp === 'mail' && fetchEmails(searchQuery)}
            />
            {(searchQuery || contactSearch) && (
              <button className="gmail-icon-btn" onClick={() => { setSearchQuery(''); setContactSearch(''); if (activeApp === 'mail') fetchEmails(''); }}>
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        {/* Right Header Controls */}
        <div className="gmail-header-right">
          <button
            className="gmail-icon-btn"
            onClick={() => {
              const next = theme === 'light' ? 'dark' : (theme === 'dark' ? 'glass' : 'light');
              setTheme(next);
              showToast(`Theme: ${next.toUpperCase()}`, 'info');
            }}
            title={`Current theme: ${theme.toUpperCase()} (click to cycle)`}
          >
            {theme === 'light' ? <Moon size={20} /> : (theme === 'dark' ? <Sparkles size={20} /> : <Sun size={20} />)}
          </button>

          {isAdmin && (
            <button
              className="gmail-pill-btn"
              onClick={() => setPortalMode('admin')}
              style={{ padding: '6px 14px', fontSize: '13px', gap: '6px' }}
              title="Open System Admin Console"
            >
              <Shield size={16} color="var(--google-blue)" />
              <span>Admin Console</span>
            </button>
          )}

          <div style={{ position: 'relative' }}>
            <button
              className="gmail-avatar-btn"
              onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
              title={profile.displayName ? `${profile.displayName} (${userEmail})` : userEmail}
              style={{ padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}
            >
              <GmailAvatar email={userEmail} name={profile.displayName} customAvatarUrl={profile.avatarUrl} size={34} />
            </button>

            {/* Google-Style Account Flyout */}
            {isAccountMenuOpen && (
              <div className="gmail-account-popup">
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                  <GmailAvatar email={userEmail} name={profile.displayName} customAvatarUrl={profile.avatarUrl} size={72} />
                </div>
                {profile.displayName && (
                  <div style={{ fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)', textAlign: 'center', marginBottom: '2px' }}>
                    {profile.displayName}
                  </div>
                )}
                <div className="gmail-account-email">{userEmail}</div>
                {isAdmin && <span className="gmail-account-role">System Administrator</span>}
                {(userEmail?.toLowerCase().endsWith('@gmail.com') || userEmail?.toLowerCase().endsWith('@googlemail.com')) && (
                  <span className="gmail-account-role" style={{ backgroundColor: 'rgba(66, 133, 244, 0.12)', color: 'var(--google-blue)', marginTop: '4px' }}>
                    Google Account
                  </span>
                )}

                <div style={{ width: '100%', margin: '16px 0', borderTop: '1px solid var(--border-subtle)' }} />

                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <button
                    className="gmail-pill-btn"
                    style={{ width: '100%', justifyContent: 'center' }}
                    onClick={() => { setActiveApp('settings'); setIsAccountMenuOpen(false); }}
                  >
                    <Settings size={16} />
                    <span>Workspace Settings</span>
                  </button>

                  <button
                    className="gmail-pill-btn"
                    style={{ width: '100%', justifyContent: 'center', color: 'var(--google-red)' }}
                    onClick={handleLogout}
                  >
                    <LogOut size={16} />
                    <span>Sign out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* 2. BODY VIEWPORT */}
      <div className="gmail-body">
        {/* Left App Rail (Google Workspace App Switcher) */}
        <aside className="gmail-app-rail">
          <div
            className={`gmail-rail-item ${activeApp === 'mail' ? 'active' : ''}`}
            onClick={() => setActiveApp('mail')}
            title="Mail (Keyboard: C to compose)"
          >
            <Mail size={20} />
            <span>Mail</span>
          </div>



          <div
            className={`gmail-rail-item ${activeApp === 'calendar' ? 'active' : ''}`}
            onClick={() => setActiveApp('calendar')}
            title="Calendar"
          >
            <CalendarIcon size={20} />
            <span>Calendar</span>
          </div>

          <div
            className={`gmail-rail-item ${activeApp === 'contacts' ? 'active' : ''}`}
            onClick={() => setActiveApp('contacts')}
            title="Contacts"
          >
            <Users size={20} />
            <span>Contacts</span>
          </div>

          <div
            className={`gmail-rail-item ${activeApp === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveApp('settings')}
            title="Settings"
          >
            <Settings size={20} />
            <span>Settings</span>
          </div>
        </aside>

        {/* ================= MAIL SUB-APP ================= */}
        {activeApp === 'mail' && (
          <>
            {/* Mail Sidebar */}
            <aside className={`gmail-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
              <button
                className="gmail-compose-btn"
                onClick={() => setComposeState(prev => ({ ...prev, isOpen: true, isMinimized: false }))}
              >
                <Plus size={24} />
                {!isSidebarCollapsed && <span>Compose</span>}
              </button>

              <nav className="gmail-nav-list">
                <div
                  className={`gmail-nav-item ${activeFolder === 'INBOX' ? 'active' : ''}`}
                  onClick={() => setActiveFolder('INBOX')}
                >
                  <Mail size={18} />
                  <span>Inbox</span>
                  {unreadCount > 0 && <span className="gmail-nav-item-badge">{unreadCount}</span>}
                </div>

                <div
                  className={`gmail-nav-item ${activeFolder === 'STARRED' ? 'active' : ''}`}
                  onClick={() => setActiveFolder('STARRED')}
                >
                  <Star size={18} />
                  <span>Starred</span>
                </div>

                <div
                  className={`gmail-nav-item ${activeFolder === 'SCHEDULED' ? 'active' : ''}`}
                  onClick={() => { setActiveFolder('SCHEDULED'); fetchScheduledEmails(); }}
                >
                  <Clock size={18} />
                  <span>Scheduled</span>
                  {scheduledEmails.filter(s => s.status === 'pending').length > 0 && (
                    <span className="gmail-nav-item-badge" style={{ backgroundColor: 'var(--google-yellow)', color: '#fff' }}>
                      {scheduledEmails.filter(s => s.status === 'pending').length}
                    </span>
                  )}
                </div>

                <div
                  className={`gmail-nav-item ${activeFolder.includes('Sent') || activeFolder === '[Gmail]/Sent Mail' ? 'active' : ''}`}
                  onClick={() => setActiveFolder(userEmail?.toLowerCase().endsWith('@gmail.com') || userEmail?.toLowerCase().endsWith('@googlemail.com') ? '[Gmail]/Sent Mail' : 'INBOX.Sent')}
                >
                  <Send size={18} />
                  <span>Sent</span>
                </div>

                <div
                  className={`gmail-nav-item ${activeFolder.includes('Drafts') || activeFolder === '[Gmail]/Drafts' ? 'active' : ''}`}
                  onClick={() => setActiveFolder(userEmail?.toLowerCase().endsWith('@gmail.com') || userEmail?.toLowerCase().endsWith('@googlemail.com') ? '[Gmail]/Drafts' : 'INBOX.Drafts')}
                >
                  <FileText size={18} />
                  <span>Drafts</span>
                </div>

                <div
                  className={`gmail-nav-item ${activeFolder.includes('Junk') || activeFolder.includes('Spam') || activeFolder === '[Gmail]/Spam' ? 'active' : ''}`}
                  onClick={() => setActiveFolder(userEmail?.toLowerCase().endsWith('@gmail.com') || userEmail?.toLowerCase().endsWith('@googlemail.com') ? '[Gmail]/Spam' : 'INBOX.Junk E-mail')}
                >
                  <ShieldAlert size={18} />
                  <span>Spam</span>
                </div>

                <div
                  className={`gmail-nav-item ${activeFolder.includes('Deleted') || activeFolder.includes('Trash') || activeFolder.includes('Bin') || activeFolder === '[Gmail]/Trash' ? 'active' : ''}`}
                  onClick={() => setActiveFolder(userEmail?.toLowerCase().endsWith('@gmail.com') || userEmail?.toLowerCase().endsWith('@googlemail.com') ? '[Gmail]/Trash' : 'INBOX.Deleted Items')}
                >
                  <Trash size={18} />
                  <span>Trash</span>
                </div>

                {/* Labels Section */}
                <div className="gmail-sidebar-section-title">
                  <span>Labels</span>
                  <button
                    className="gmail-icon-btn"
                    style={{ width: '24px', height: '24px' }}
                    onClick={() => {
                      setEditingLabel(null);
                      setLabelForm({ name: '', color: '#6366f1' });
                      setIsLabelModalOpen(true);
                    }}
                    title="Create new label"
                  >
                    <Plus size={16} />
                  </button>
                </div>

                {labels.map(lbl => (
                  <div
                    key={lbl.id}
                    className={`gmail-nav-item ${activeFolder === `label:${lbl.name}` ? 'active' : ''}`}
                    onClick={() => setActiveFolder(`label:${lbl.name}`)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '8px' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                      <span
                        style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '2px',
                          backgroundColor: lbl.color,
                          flexShrink: 0
                        }}
                      />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lbl.name}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {lbl.count > 0 && <span className="gmail-nav-item-badge">{lbl.count}</span>}
                      <button
                        className="gmail-icon-btn"
                        style={{ width: '20px', height: '20px', padding: 0, opacity: 0.7 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingLabel(lbl);
                          setLabelForm({ name: lbl.name, color: lbl.color });
                          setIsLabelModalOpen(true);
                        }}
                        title="Edit label"
                      >
                        <Edit3 size={12} />
                      </button>
                      <button
                        className="gmail-icon-btn"
                        style={{ width: '20px', height: '20px', padding: 0, opacity: 0.7 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteLabel(lbl.id);
                        }}
                        title="Delete label"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}

                {/* Custom Folders Section */}
                <div className="gmail-sidebar-section-title" style={{ marginTop: '12px' }}>
                  <span>Folders</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <button className="gmail-icon-btn" style={{ width: '24px', height: '24px' }} onClick={() => handleExportFolder(activeFolder)} title="One-Click Folder Backup (.zip)">
                      <Download size={14} />
                    </button>
                    <button className="gmail-icon-btn" style={{ width: '24px', height: '24px' }} onClick={() => setIsNewFolderOpen(true)} title="Create folder">
                      <FolderPlus size={16} />
                    </button>
                  </div>
                </div>

                {folders.filter(f => {
                  const systemPaths = [
                    'INBOX', 'INBOX.Sent', 'INBOX.Drafts', 'INBOX.Archive', 'INBOX.Junk E-mail', 'INBOX.Deleted Items',
                    '[Gmail]', '[Google Mail]', '[Gmail]/Sent Mail', '[Gmail]/Drafts', '[Gmail]/Trash', '[Gmail]/Bin',
                    '[Gmail]/Spam', '[Gmail]/Starred', '[Gmail]/All Mail', '[Gmail]/Important',
                    '[Google Mail]/Sent Mail', '[Google Mail]/Drafts', '[Google Mail]/Trash', '[Google Mail]/Bin',
                    '[Google Mail]/Spam', '[Google Mail]/Starred', '[Google Mail]/All Mail', '[Google Mail]/Important'
                  ];
                  return !systemPaths.includes(f.path) && f.path !== '[Gmail]' && f.path !== '[Google Mail]';
                }).map(f => (
                  <div
                    key={f.path}
                    className={`gmail-nav-item ${activeFolder === f.path ? 'active' : ''}`}
                    onClick={() => setActiveFolder(f.path)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '8px' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                      <Folder size={18} />
                      <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{f.name}</span>
                    </div>
                    <button
                      className="gmail-icon-btn"
                      style={{ width: '20px', height: '20px', padding: 0, opacity: 0.7 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExportFolder(f.path);
                      }}
                      title={`Backup & Export ${f.name} (.zip)`}
                    >
                      <Download size={12} />
                    </button>
                  </div>
                ))}
              </nav>

              <div className="gmail-storage-box">
                <div className="gmail-progress-bar">
                  <div className="gmail-progress-fill" style={{ width: `${storageUsage.percent}%` }} />
                </div>
                <span>
                  {storageUsage.totalMb >= 1024
                    ? `${(storageUsage.usedMb / 1024).toFixed(2)} GB of ${(storageUsage.totalMb / 1024).toFixed(1)} GB used`
                    : `${storageUsage.usedMb} MB of ${storageUsage.totalMb} MB used`}
                </span>
              </div>
            </aside>

            {/* Mail Main View */}
            <main className="gmail-main">
              <div className="gmail-toolbar">
                <div className="gmail-toolbar-left">
                  {!selectedEmail ? (
                    <>
                      <button className="gmail-icon-btn" onClick={toggleSelectAll} title="Select">
                        {selectedUids.size > 0 && selectedUids.size === filteredEmails.length ? (
                          <CheckSquare size={18} color="var(--google-blue)" />
                        ) : (
                          <Square size={18} />
                        )}
                      </button>
                      <button className="gmail-icon-btn" onClick={() => (activeFolder === 'SCHEDULED' ? fetchScheduledEmails() : fetchEmails())} title="Refresh">
                        <RefreshCw size={18} className={isLoadingEmails || isLoadingScheduled ? 'spin' : ''} />
                      </button>
                      <button className="gmail-icon-btn" onClick={() => handleExportFolder(activeFolder)} title={`One-Click Backup & Export ${activeFolder} (.zip)`}>
                        <Download size={18} />
                      </button>
                      {selectedUids.size > 0 && (
                        <>
                          <button className="gmail-icon-btn" onClick={deleteSelected} title="Delete selected">
                            <Trash2 size={18} color="var(--google-red)" />
                          </button>
                          <button className="gmail-icon-btn" onClick={handleExportSelectedEmails} title="Export selected (.zip archive)">
                            <FileDown size={18} color="var(--google-blue)" />
                          </button>

                          {/* Bulk Label Selector Popover */}
                          <div style={{ position: 'relative' }}>
                            <button
                              className="gmail-icon-btn"
                              onClick={() => setIsLabelPickerOpen(prev => !prev)}
                              title="Labels"
                            >
                              <Tag size={18} />
                            </button>

                            {isLabelPickerOpen && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  zIndex: 1000,
                                  backgroundColor: 'var(--bg-surface)',
                                  border: '1px solid var(--border-color)',
                                  borderRadius: 'var(--radius-md)',
                                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.25)',
                                  width: '240px',
                                  padding: '10px',
                                  marginTop: '6px'
                                }}
                                onClick={e => e.stopPropagation()}
                              >
                                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px', padding: '0 4px' }}>
                                  Label as:
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', padding: '4px 8px', backgroundColor: 'var(--bg-surface-variant)', borderRadius: 'var(--radius-sm)' }}>
                                  <Search size={14} color="var(--text-muted)" />
                                  <input
                                    type="text"
                                    placeholder="Filter labels..."
                                    value={labelSearchQuery}
                                    onChange={e => setLabelSearchQuery(e.target.value)}
                                    style={{ background: 'none', border: 'none', outline: 'none', fontSize: '12px', color: 'var(--text-primary)', width: '100%' }}
                                  />
                                </div>
                                <div style={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  {labels
                                    .filter(l => l.name.toLowerCase().includes(labelSearchQuery.toLowerCase()))
                                    .map(lbl => {
                                      const targetUids = Array.from(selectedUids);
                                      const isChecked = targetUids.length > 0 && targetUids.every(uid => {
                                        const em = emails.find(e => e.uid === uid);
                                        return em?.labels?.includes(lbl.name);
                                      });
                                      return (
                                        <div
                                          key={lbl.id}
                                          onClick={() => handleToggleLabel(lbl.name)}
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '6px 8px',
                                            borderRadius: 'var(--radius-sm)',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            backgroundColor: isChecked ? 'var(--bg-surface-variant)' : 'transparent'
                                          }}
                                        >
                                          <div style={{ width: '16px', height: '16px', borderRadius: '3px', border: `2px solid ${lbl.color}`, backgroundColor: isChecked ? lbl.color : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                                            {isChecked && <Check size={12} strokeWidth={3} />}
                                          </div>
                                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: lbl.color }} />
                                          <span style={{ flex: 1, color: 'var(--text-primary)' }}>{lbl.name}</span>
                                        </div>
                                      );
                                    })}
                                  {labels.length === 0 && (
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '12px 0' }}>
                                      No labels yet
                                    </div>
                                  )}
                                </div>
                                <div style={{ borderTop: '1px solid var(--border-subtle)', marginTop: '8px', paddingTop: '8px' }}>
                                  <button
                                    type="button"
                                    onClick={() => { setIsLabelPickerOpen(false); setEditingLabel(null); setLabelForm({ name: '', color: '#6366f1' }); setIsLabelModalOpen(true); }}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      width: '100%',
                                      background: 'none',
                                      border: 'none',
                                      color: 'var(--google-blue)',
                                      cursor: 'pointer',
                                      fontSize: '12px',
                                      fontWeight: 500,
                                      padding: '4px'
                                    }}
                                  >
                                    <Plus size={14} />
                                    <span>Create new label</span>
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}

                      {/* Quick Filter Chips */}
                      <div style={{ display: 'flex', gap: '6px', marginLeft: '12px' }}>
                        {[
                          { id: 'all', label: 'All' },
                          { id: 'unread', label: 'Unread' },
                          { id: 'starred', label: 'Starred' },
                          { id: 'attachments', label: 'Has file' }
                        ].map(chip => (
                          <button
                            key={chip.id}
                            onClick={() => setQuickFilter(chip.id)}
                            style={{
                              padding: '3px 10px',
                              borderRadius: 'var(--radius-pill)',
                              fontSize: '12px',
                              fontWeight: 500,
                              backgroundColor: quickFilter === chip.id ? 'var(--google-blue-light)' : 'transparent',
                              color: quickFilter === chip.id ? 'var(--google-blue)' : 'var(--text-secondary)',
                              border: `1px solid ${quickFilter === chip.id ? 'var(--google-blue)' : 'var(--border-color)'}`
                            }}
                          >
                            {chip.label}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <button className="gmail-icon-btn" onClick={() => setSelectedEmail(null)} title="Back to inbox (Esc / U)">
                        <ArrowLeft size={20} />
                      </button>
                      <button 
                        className={`gmail-icon-btn ${selectedEmail.isStarred ? 'starred' : ''}`} 
                        onClick={(e) => toggleStar(e, selectedEmail)} 
                        title={selectedEmail.isStarred ? 'Unstar (s)' : 'Star (s)'}
                      >
                        <Star size={18} fill={selectedEmail.isStarred ? 'var(--star-active)' : 'none'} color={selectedEmail.isStarred ? 'var(--star-active)' : 'var(--text-secondary)'} />
                      </button>
                      <button className="gmail-icon-btn" onClick={deleteSelected} title="Delete (#)">
                        <Trash2 size={18} />
                      </button>

                      {/* Single Email Label Popover */}
                      <div style={{ position: 'relative' }}>
                        <button
                          className="gmail-icon-btn"
                          onClick={() => setIsLabelPickerOpen(prev => !prev)}
                          title="Labels"
                        >
                          <Tag size={18} />
                        </button>

                        {isLabelPickerOpen && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              zIndex: 1000,
                              backgroundColor: 'var(--bg-surface)',
                              border: '1px solid var(--border-color)',
                              borderRadius: 'var(--radius-md)',
                              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.25)',
                              width: '240px',
                              padding: '10px',
                              marginTop: '6px'
                            }}
                            onClick={e => e.stopPropagation()}
                          >
                            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px', padding: '0 4px' }}>
                              Label as:
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', padding: '4px 8px', backgroundColor: 'var(--bg-surface-variant)', borderRadius: 'var(--radius-sm)' }}>
                              <Search size={14} color="var(--text-muted)" />
                              <input
                                type="text"
                                placeholder="Filter labels..."
                                value={labelSearchQuery}
                                onChange={e => setLabelSearchQuery(e.target.value)}
                                style={{ background: 'none', border: 'none', outline: 'none', fontSize: '12px', color: 'var(--text-primary)', width: '100%' }}
                              />
                            </div>
                            <div style={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              {labels
                                .filter(l => l.name.toLowerCase().includes(labelSearchQuery.toLowerCase()))
                                .map(lbl => {
                                  const isChecked = selectedEmail?.labels?.includes(lbl.name);
                                  return (
                                    <div
                                      key={lbl.id}
                                      onClick={() => handleToggleLabel(lbl.name)}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '6px 8px',
                                        borderRadius: 'var(--radius-sm)',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        backgroundColor: isChecked ? 'var(--bg-surface-variant)' : 'transparent'
                                      }}
                                    >
                                      <div style={{ width: '16px', height: '16px', borderRadius: '3px', border: `2px solid ${lbl.color}`, backgroundColor: isChecked ? lbl.color : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                                        {isChecked && <Check size={12} strokeWidth={3} />}
                                      </div>
                                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: lbl.color }} />
                                      <span style={{ flex: 1, color: 'var(--text-primary)' }}>{lbl.name}</span>
                                    </div>
                                  );
                                })}
                              {labels.length === 0 && (
                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '12px 0' }}>
                                  No labels yet
                                </div>
                              )}
                            </div>
                            <div style={{ borderTop: '1px solid var(--border-subtle)', marginTop: '8px', paddingTop: '8px' }}>
                              <button
                                type="button"
                                onClick={() => { setIsLabelPickerOpen(false); setEditingLabel(null); setLabelForm({ name: '', color: '#6366f1' }); setIsLabelModalOpen(true); }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  width: '100%',
                                  background: 'none',
                                  border: 'none',
                                  color: 'var(--google-blue)',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  padding: '4px'
                                }}
                              >
                                <Plus size={14} />
                                <span>Create new label</span>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      <button className="gmail-icon-btn" onClick={() => handleReply(false)} title="Reply (R)">
                        <CornerUpLeft size={18} />
                      </button>
                      <button className="gmail-icon-btn" onClick={() => handleReply(true)} title="Reply All (A)">
                        <CornerUpLeft size={18} />
                      </button>
                      <button className="gmail-icon-btn" onClick={handleForward} title="Forward (F)">
                        <CornerUpRight size={18} />
                      </button>
                      <button className="gmail-icon-btn" onClick={handlePrintEmail} title="Print Email">
                        <Printer size={18} />
                      </button>
                      <button className="gmail-icon-btn" onClick={handleDownloadEml} title="Download .eml">
                        <FileDown size={18} />
                      </button>
                    </>
                  )}
                </div>

                <div className="gmail-toolbar-right">
                  <span className="gmail-counter-text">
                    {filteredEmails.length > 0 ? `1–${filteredEmails.length} of ${filteredEmails.length}` : '0 of 0'}
                  </span>
                </div>
              </div>

              {!selectedEmail ? (
                activeFolder === 'SCHEDULED' ? (
                  /* Scheduled Emails List */
                  <div className="gmail-email-list">
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: 'var(--bg-surface)' }}>
                      <div>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Clock size={18} color="var(--google-blue)" />
                          <span>Scheduled Outbox</span>
                        </h3>
                        <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '4px 0 0 0' }}>
                          Messages queued for automated dispatch at their scheduled date and time.
                        </p>
                      </div>
                      <button className="gmail-pill-btn" onClick={fetchScheduledEmails}>
                        <RefreshCw size={14} className={isLoadingScheduled ? 'spin' : ''} />
                        <span>Refresh Queue</span>
                      </button>
                    </div>

                    {isLoadingScheduled ? (
                      <div className="gmail-empty-state">
                        <RefreshCw size={32} className="spin" />
                        <span>Checking scheduled queue...</span>
                      </div>
                    ) : scheduledEmails.length === 0 ? (
                      <div className="gmail-empty-state">
                        <Clock size={44} style={{ opacity: 0.4 }} />
                        <h3>No scheduled messages</h3>
                        <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Use the arrow next to Send in the compose modal to schedule emails for later delivery.</p>
                      </div>
                    ) : (
                      scheduledEmails.map(item => (
                        <div
                          key={item.id}
                          className="gmail-email-row"
                          style={{ padding: '12px 18px', gap: '16px', cursor: 'pointer' }}
                          onClick={() => handleOpenScheduledEmail(item)}
                          title="Click to view or edit scheduled message"
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '220px' }}>
                            <GmailAvatar email={item.to_recipients} size={28} />
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{item.to_recipients}</span>
                              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>To Recipient</span>
                            </div>
                          </div>

                          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                            <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>{item.subject || '(no subject)'}</span>
                            <span style={{ fontSize: '12px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              - {item.body_plain ? item.body_plain.slice(0, 80) : ''}
                            </span>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }} onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginRight: '8px' }}>
                              <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--google-blue)' }}>
                                ⏱️ {new Date(item.scheduled_for).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                              </span>
                              <span style={{ fontSize: '11px', fontWeight: 600, color: item.status === 'sent' ? 'var(--google-green)' : (item.status === 'failed' ? 'var(--google-red)' : 'var(--google-yellow)') }}>
                                {item.status.toUpperCase()}
                              </span>
                            </div>

                            <button
                              className="gmail-pill-btn"
                              style={{ padding: '4px 10px', fontSize: '12px', borderColor: 'var(--google-blue)', color: 'var(--google-blue)' }}
                              onClick={() => handleOpenScheduledEmail(item)}
                              title="Edit & reschedule message"
                            >
                              <Edit size={12} />
                              <span>Edit</span>
                            </button>

                            {item.status === 'pending' && (
                              <button
                                className="gmail-pill-btn"
                                style={{ padding: '4px 10px', fontSize: '12px' }}
                                onClick={() => handleSendScheduledNow(item.id)}
                                title="Send immediately now"
                              >
                                <Send size={12} />
                                <span>Send Now</span>
                              </button>
                            )}

                            <button
                              className="gmail-icon-btn"
                              onClick={() => handleCancelScheduledEmail(item.id)}
                              title="Cancel scheduled email"
                            >
                              <Trash2 size={16} color="var(--google-red)" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                /* Regular Email List */
                <div className="gmail-email-list">
                  {isLoadingEmails ? (
                    <div className="gmail-empty-state">
                      <RefreshCw size={36} className="spin gmail-empty-icon" />
                      <span>Loading conversations...</span>
                    </div>
                  ) : filteredEmails.length === 0 ? (
                    <div className="gmail-empty-state">
                      <Mail size={48} className="gmail-empty-icon" />
                      <h3>No conversations in {activeFolder.startsWith('label:') ? `Label: ${activeFolder.replace('label:', '')}` : activeFolder.replace('INBOX.', '')}</h3>
                      <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Try resetting search or quick filters</p>
                    </div>
                  ) : (
                    filteredEmails.map(email => (
                      <div
                        key={`${email.folder || 'INBOX'}-${email.uid}`}
                        className={`gmail-email-row ${!email.isRead ? 'unread' : ''} ${selectedUids.has(email.uid) ? 'selected' : ''}`}
                        onClick={() => openEmail(email)}
                      >
                        <div onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="gmail-checkbox"
                            checked={selectedUids.has(email.uid)}
                            onChange={e => toggleSelectUid(e, email.uid)}
                          />
                        </div>

                        <button
                          className={`gmail-star-btn ${email.isStarred ? 'starred' : ''}`}
                          onClick={e => toggleStar(e, email)}
                        >
                          <Star size={18} fill={email.isStarred ? 'var(--star-active)' : 'none'} />
                        </button>

                        <div className="gmail-sender-col" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <GmailAvatar email={email.from?.address} name={email.from?.name} size={24} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {email.from?.name || email.from?.address || 'Unknown'}
                          </span>
                        </div>

                        <div className="gmail-content-col" style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden' }}>
                          {email.labels && email.labels.length > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                              {email.labels.map(lName => {
                                if (lName.startsWith('Mask: ')) {
                                  const serviceName = lName.replace('Mask: ', '');
                                  return (
                                    <span
                                      key={lName}
                                      onClick={(e) => { e.stopPropagation(); setActiveFolder(`label:${lName}`); }}
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        padding: '1px 7px',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        backgroundColor: 'rgba(139, 92, 246, 0.15)',
                                        color: '#8b5cf6',
                                        border: '1px solid rgba(139, 92, 246, 0.4)',
                                        cursor: 'pointer'
                                      }}
                                      title={`Delivered via Masked Alias: ${serviceName}`}
                                    >
                                      <Shield size={10} strokeWidth={2.5} />
                                      <span>{serviceName}</span>
                                    </span>
                                  );
                                }
                                const lblObj = labels.find(l => l.name === lName);
                                const col = lblObj ? lblObj.color : '#6366f1';
                                return (
                                  <span
                                    key={lName}
                                    onClick={(e) => { e.stopPropagation(); setActiveFolder(`label:${lName}`); }}
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      padding: '1px 6px',
                                      borderRadius: '3px',
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      backgroundColor: `${col}18`,
                                      color: col,
                                      border: `1px solid ${col}40`,
                                      cursor: 'pointer'
                                    }}
                                    title={`Filter by label: ${lName}`}
                                  >
                                    {lName}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                          <span className="gmail-subject-text">{email.subject || '(no subject)'}</span>
                          <span className="gmail-snippet-text">— {email.snippet || ''}</span>
                        </div>

                        {email.hasAttachments && (
                          <Paperclip size={16} color="var(--text-muted)" style={{ flexShrink: 0 }} />
                        )}

                        <div className="gmail-date-col">{formatDate(email.date)}</div>

                        <div className="gmail-row-actions" onClick={e => e.stopPropagation()}>
                          <button className="gmail-icon-btn" onClick={() => deleteSingleEmail(email.uid, email.folder)} title="Delete conversation">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                )
              ) : (
                /* Email Reader */
                <div className="gmail-detail-view">

                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', marginBottom: '8px' }}>
                    <div>
                      <h2 className="gmail-detail-subject">{selectedEmail.subject || '(no subject)'}</h2>
                      {selectedEmail.labels && selectedEmail.labels.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                          {selectedEmail.labels.map(lName => {
                            const lblObj = labels.find(l => l.name === lName);
                            const col = lblObj ? lblObj.color : '#6366f1';
                            return (
                              <span
                                key={lName}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '2px 8px',
                                  borderRadius: 'var(--radius-pill)',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  backgroundColor: `${col}1a`,
                                  color: col,
                                  border: `1px solid ${col}40`
                                }}
                              >
                                <span>{lName}</span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleRemoveLabelFromEmail(selectedEmail.uid, lName); }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    cursor: 'pointer',
                                    color: col,
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                  title="Remove label"
                                >
                                  <X size={12} />
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="gmail-detail-header">
                    <div className="gmail-detail-sender-info">
                      <GmailAvatar email={selectedEmail.from?.address} name={selectedEmail.from?.name} size={42} />
                      <div className="gmail-sender-meta">
                        <div className="gmail-sender-name-line">
                          <span className="gmail-sender-name">{selectedEmail.from?.name || selectedEmail.from?.address}</span>
                          <span className="gmail-sender-address">&lt;{selectedEmail.from?.address}&gt;</span>
                        </div>
                        <div className="gmail-recipient-line">
                          <span>to {selectedEmail.to?.map(t => t.address).join(', ') || 'me'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="gmail-detail-date">
                      {new Date(selectedEmail.date).toLocaleString()}
                    </div>
                  </div>

                  <div className="gmail-detail-body">
                    {selectedEmail.bodyHtml ? (
                      <div dangerouslySetInnerHTML={{ __html: selectedEmail.bodyHtml }} />
                    ) : (
                      <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                        {selectedEmail.bodyText || selectedEmail.snippet}
                      </pre>
                    )}
                  </div>

                  {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                    <div className="gmail-attachments-section">
                      <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-muted)' }}>
                        {selectedEmail.attachments.length} Attachment(s)
                      </span>
                      <div className="gmail-attachments-grid">
                        {selectedEmail.attachments.map((att, idx) => (
                          <a
                            key={idx}
                            href={`data:${att.contentType || 'application/octet-stream'};base64,${att.content}`}
                            download={att.filename}
                            className="gmail-attachment-card"
                          >
                            <Paperclip size={16} />
                            <span>{att.filename}</span>
                            <Download size={14} color="var(--google-blue)" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="gmail-quick-reply-bar">
                    <button className="gmail-pill-btn" onClick={() => handleReply(false)}>
                      <CornerUpLeft size={16} />
                      <span>Reply</span>
                    </button>
                    <button className="gmail-pill-btn" onClick={() => handleReply(true)}>
                      <CornerUpLeft size={16} />
                      <span>Reply all</span>
                    </button>
                    <button className="gmail-pill-btn" onClick={handleForward}>
                      <CornerUpRight size={16} />
                      <span>Forward</span>
                    </button>
                  </div>
                </div>
              )}
            </main>
          </>
        )}

        {/* ================= CALENDAR SUB-APP ================= */}
        {activeApp === 'calendar' && (
          <main className="gmail-main">
            <div className="gmail-calendar-view">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <h2 style={{ fontSize: '20px', fontWeight: 500 }}>
                    {currentMonthDate.toLocaleString('default', { month: 'long' })} {currentMonthDate.getFullYear()}
                  </h2>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      className="gmail-icon-btn"
                      onClick={() => setCurrentMonthDate(new Date(currentMonthDate.setMonth(currentMonthDate.getMonth() - 1)))}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      className="gmail-icon-btn"
                      onClick={() => setCurrentMonthDate(new Date(currentMonthDate.setMonth(currentMonthDate.getMonth() + 1)))}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>

                <button className="gmail-pill-btn primary" onClick={() => setIsAddEventOpen(true)}>
                  <Plus size={16} />
                  <span>Schedule Event</span>
                </button>
              </div>

              <div className="gmail-calendar-grid-header">
                <div>Sun</div><div>Mon</div><div>Tue</div><div>Wed</div><div>Thu</div><div>Fri</div><div>Sat</div>
              </div>

              <div className="gmail-calendar-grid">
                {getDaysInMonth(currentMonthDate).map((day, idx) => {
                  const dayEvents = day ? calendarEvents.filter(e => {
                    return new Date(e.start_time).toDateString() === day.toDateString();
                  }) : [];
                  const isToday = day && day.toDateString() === new Date().toDateString();

                  return (
                    <div key={idx} className={`gmail-calendar-cell ${isToday ? 'today' : ''}`}>
                      {day && (
                        <>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: isToday ? 'var(--google-blue)' : 'var(--text-muted)' }}>
                            {day.getDate()}
                          </span>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {dayEvents.map(ev => (
                              <div key={ev.id} className="gmail-calendar-event-chip" onClick={() => handleDeleteEvent(ev.id)}>
                                <span>{ev.title}</span>
                                <Trash size={10} />
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </main>
        )}

        {/* ================= CONTACTS SUB-APP ================= */}
        {activeApp === 'contacts' && (
          <main className="gmail-main">
            <div className="gmail-contacts-view">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ fontSize: '20px', fontWeight: 500 }}>Contacts Directory</h2>
                  <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Manage personal & team address book</p>
                </div>
                <button className="gmail-pill-btn primary" onClick={() => setIsAddContactOpen(true)}>
                  <UserPlus size={16} />
                  <span>Add Contact</span>
                </button>
              </div>

              <div className="gmail-contacts-grid">
                {contacts
                  .filter(c => !contactSearch || (c.name || '').toLowerCase().includes(contactSearch.toLowerCase()) || (c.email || '').toLowerCase().includes(contactSearch.toLowerCase()))
                  .map(c => (
                    <div key={c.id} className="gmail-contact-card">
                      <GmailAvatar email={c.email} name={c.name} size={40} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '14px' }}>{c.name || 'Unnamed'}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{c.email}</div>
                        {c.phone && <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{c.phone}</div>}
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          className="gmail-icon-btn"
                          style={{ width: '32px', height: '32px' }}
                          onClick={() => {
                            setComposeState(prev => ({ ...prev, isOpen: true, to: c.email }));
                            setActiveApp('mail');
                          }}
                          title="Send email"
                        >
                          <Mail size={15} color="var(--google-blue)" />
                        </button>
                        <button
                          className="gmail-icon-btn"
                          style={{ width: '32px', height: '32px' }}
                          onClick={() => handleDeleteContact(c.id)}
                          title="Delete contact"
                        >
                          <Trash2 size={15} color="var(--google-red)" />
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </main>
        )}

        {/* ================= SETTINGS SUB-APP ================= */}
        {activeApp === 'settings' && (
          <main className="gmail-main">
            <div className="gmail-settings-view">
              <nav className="gmail-settings-nav">
                {[
                  { id: 'profile', label: 'Profile & Account', icon: <Users size={16} /> },
                  { id: 'forwarding', label: 'Email Forwarding', icon: <ArrowLeftRight size={16} /> },
                  { id: 'app_passwords', label: 'App Passwords', icon: <Key size={16} /> },
                  { id: 'aliases', label: 'Virtual Aliases', icon: <Tag size={16} /> },
                  { id: 'masked_emails', label: 'Masked Aliases (Hide Email)', icon: <Shield size={16} /> },
                  { id: 'cloud_storage', label: 'Link Cloud Storage', icon: <Cloud size={16} /> },
                  { id: 'signature', label: 'Email Signature', icon: <Edit3 size={16} /> },
                  { id: 'autoreply', label: 'Vacation Responder', icon: <Reply size={16} /> },
                  { id: 'security', label: 'Security & 2FA', icon: <Lock size={16} /> },
                  { id: 'appearance', label: 'Themes & Colors', icon: <Palette size={16} /> },
                  { id: 'storage', label: 'Storage Allocation', icon: <HardDrive size={16} /> },
                ].map(tab => (
                  <div
                    key={tab.id}
                    className={`gmail-nav-item ${activeSettingsTab === tab.id ? 'active' : ''}`}
                    onClick={() => setActiveSettingsTab(tab.id)}
                  >
                    {tab.icon}
                    <span>{tab.label}</span>
                  </div>
                ))}
              </nav>

              <div className="gmail-settings-content">
                {/* 1. PROFILE */}
                {activeSettingsTab === 'profile' && (
                  <form onSubmit={handleSaveProfile} className="gmail-settings-card">
                    <h3 style={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Users size={18} color="var(--google-blue)" />
                      <span>Account Profile & Avatar</span>
                    </h3>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                      Personalize your public profile photo, display name, and mailbox details.
                    </p>

                    {/* Profile Photo Live Customizer */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      padding: '16px',
                      backgroundColor: 'var(--bg-surface-variant)',
                      borderRadius: '8px',
                      margin: '12px 0 16px 0',
                      border: '1px solid var(--border-color)'
                    }}>
                      <div style={{ position: 'relative' }}>
                        <GmailAvatar
                          email={userEmail}
                          name={profile.displayName}
                          customAvatarUrl={profile.avatarUrl}
                          size={68}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>
                          Profile Photo URL / Avatar
                        </label>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <input
                            type="url"
                            className="gmail-form-input"
                            style={{ flex: 1, minWidth: '220px' }}
                            placeholder="https://example.com/photo.jpg or Gravatar URL"
                            value={profile.avatarUrl || ''}
                            onChange={e => setProfile({ ...profile, avatarUrl: e.target.value })}
                          />
                          <button
                            type="button"
                            className="gmail-pill-btn"
                            onClick={() => setProfile({ ...profile, avatarUrl: `https://unavatar.io/${encodeURIComponent(userEmail)}?fallback=false` })}
                            title="Auto-fetch Gravatar or Social avatar"
                          >
                            <Sparkles size={14} color="var(--google-blue)" />
                            <span>Use Gravatar</span>
                          </button>
                          {profile.avatarUrl && (
                            <button
                              type="button"
                              className="gmail-pill-btn"
                              onClick={() => setProfile({ ...profile, avatarUrl: '' })}
                              title="Reset to colorful initials circle"
                            >
                              <X size={14} color="var(--google-red)" />
                              <span>Clear</span>
                            </button>
                          )}
                        </div>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
                          Enter any direct image link or click "Use Gravatar" to pull your registered avatar.
                        </span>
                      </div>
                    </div>

                    <div className="gmail-input-group">
                      <label>Display Name (Sender Name)</label>
                      <input
                        type="text"
                        className="gmail-form-input"
                        placeholder="e.g. Alexander Pierce"
                        value={profile.displayName || ''}
                        onChange={e => setProfile({ ...profile, displayName: e.target.value })}
                      />
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        This name is shown to email recipients next to your profile photo.
                      </span>
                    </div>

                    <div className="gmail-input-group">
                      <label>Email Address</label>
                      <input className="gmail-form-input" value={userEmail} disabled />
                    </div>

                    <div className="gmail-input-group">
                      <label>Mailbox Role</label>
                      <input className="gmail-form-input" value={isAdmin ? 'Administrator' : 'Standard User'} disabled />
                    </div>

                    <button type="submit" className="gmail-pill-btn primary" style={{ alignSelf: 'flex-start', marginTop: '8px' }}>
                      <CheckCircle2 size={16} />
                      <span>Save Profile & Avatar</span>
                    </button>
                  </form>
                )}

                {/* 2. FORWARDING */}
                {activeSettingsTab === 'forwarding' && (
                  <form onSubmit={handleSaveSettings} className="gmail-settings-card">
                    <h3 style={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <ArrowLeftRight size={18} color="var(--google-blue)" />
                      <span>Email Forwarding</span>
                    </h3>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                      Automatically redirect a copy of incoming messages to an external email address.
                    </p>
                    <div className="gmail-input-group">
                      <label>Forward incoming mail to:</label>
                      <input
                        type="email"
                        className="gmail-form-input"
                        placeholder="e.g. backup@example.com"
                        value={userSettings.forward_to || ''}
                        onChange={e => setUserSettings({ ...userSettings, forward_to: e.target.value })}
                      />
                    </div>
                    {userSettings.forward_to && (
                      <div style={{ fontSize: '12px', color: 'var(--google-green)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <CheckCircle2 size={14} />
                        <span>Active: Messages will be forwarded to {userSettings.forward_to}</span>
                      </div>
                    )}
                    <button type="submit" className="gmail-pill-btn primary" style={{ alignSelf: 'flex-start' }}>
                      Save Forwarding Rule
                    </button>
                  </form>
                )}

                {/* 3. APP PASSWORDS */}
                {activeSettingsTab === 'app_passwords' && (
                  <div className="gmail-settings-card">
                    <h3 style={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Key size={18} color="var(--google-blue)" />
                      <span>Application Specific Passwords</span>
                    </h3>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                      Generate dedicated tokens to authenticate third-party email clients (Apple Mail, Outlook, Thunderbird, or Mobile Flutter app).
                    </p>

                    <form onSubmit={handleCreateAppPassword} style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <div className="gmail-input-group" style={{ flex: 1, minWidth: '220px' }}>
                        <label>App or Device Name</label>
                        <input
                          type="text"
                          className="gmail-form-input"
                          placeholder="e.g. iPhone Mail, Thunderbird, Flutter Client"
                          value={newAppName}
                          onChange={e => setNewAppName(e.target.value)}
                          required
                        />
                      </div>
                      <button type="submit" className="gmail-pill-btn primary" style={{ height: '42px' }}>
                        <Plus size={16} />
                        <span>Generate Token</span>
                      </button>
                    </form>

                    {/* Generated Password Callout */}
                    {generatedAppPass && (
                      <div style={{
                        padding: '16px',
                        backgroundColor: 'var(--bg-surface-variant)',
                        border: '2px solid var(--google-blue)',
                        borderRadius: 'var(--radius-sm)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}>
                        <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--google-blue)' }}>
                          New App Password for "{generatedAppPass.name}"
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <code style={{ fontSize: '18px', fontWeight: 'bold', letterSpacing: '2px', backgroundColor: 'var(--bg-surface)', padding: '6px 12px', borderRadius: '4px', border: '1px solid var(--border-color)' }}>
                            {generatedAppPass.password}
                          </code>
                          <button
                            className="gmail-pill-btn"
                            onClick={() => {
                              navigator.clipboard.writeText(generatedAppPass.password);
                              showToast('Copied to clipboard!', 'success');
                            }}
                          >
                            <Copy size={14} />
                            <span>Copy</span>
                          </button>
                        </div>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                          Save this password now; it will not be displayed again.
                        </span>
                      </div>
                    )}

                    {/* Active App Passwords Table */}
                    <div style={{ marginTop: '12px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>Active App Passwords</h4>
                      {appPasswords.length === 0 ? (
                        <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                          No application passwords generated yet.
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {appPasswords.map(p => (
                            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', backgroundColor: 'var(--bg-surface-variant)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)' }}>
                              <div>
                                <div style={{ fontWeight: 600, fontSize: '13px' }}>{p.name}</div>
                                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Created {new Date(p.created_at).toLocaleDateString()}</div>
                              </div>
                              <button
                                className="gmail-icon-btn"
                                style={{ color: 'var(--google-red)' }}
                                onClick={() => handleDeleteAppPassword(p.id)}
                                title="Revoke access"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* 4. VIRTUAL ALIASES */}
                {activeSettingsTab === 'aliases' && (
                  <div className="gmail-settings-card">
                    <h3 style={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Tag size={18} color="var(--google-blue)" />
                      <span>Virtual Email Aliases</span>
                    </h3>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                      Inbound virtual alias addresses mapped directly to your primary mailbox.
                    </p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {aliases.length === 0 ? (
                        <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                          No custom aliases configured for this mailbox.
                        </div>
                      ) : (
                        aliases.map(al => (
                          <div key={al.id || al.source} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', backgroundColor: 'var(--bg-surface-variant)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)' }}>
                            <div>
                              <strong style={{ fontSize: '14px' }}>{al.source}</strong>
                              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Delivers to: {al.destination || userEmail}</div>
                            </div>
                            <span className="gmail-account-role">Active</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* 4b. DISPOSABLE MASKED EMAIL ALIASES (HIDE MY EMAIL) */}
                {activeSettingsTab === 'masked_emails' && (
                  <div className="gmail-settings-card" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                      <div>
                        <h3 style={{ fontSize: '18px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px', margin: 0, color: 'var(--text-primary)' }}>
                          <Shield size={20} color="#8b5cf6" />
                          <span>Disposable Masked Email Aliases</span>
                        </h3>
                        <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px', maxWidth: '640px', lineHeight: 1.5 }}>
                          Generate random, private email addresses for signups, shopping, and services. Inbound emails forward automatically to your real inbox. If an alias receives spam, turn it off with one click to permanently block the sender.
                        </p>
                      </div>
                      <button
                        type="button"
                        className="gmail-send-btn"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'linear-gradient(135deg, #7c3aed, #6366f1)', border: 'none', boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)' }}
                        onClick={() => {
                          setMaskForm({ label: '', prefix: '', domain: userEmail.split('@')[1] || 'mainnode.qzz.io', description: '', expiresInDays: 0 });
                          setIsMaskModalOpen(true);
                        }}
                      >
                        <Plus size={16} />
                        <span>Generate Masked Email</span>
                      </button>
                    </div>

                    {/* Stats & Search Bar */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                      <div style={{ padding: '14px', backgroundColor: 'var(--bg-surface-variant)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 500 }}>Total Masked Aliases</div>
                        <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)', marginTop: '4px' }}>{maskedEmails.length}</div>
                      </div>
                      <div style={{ padding: '14px', backgroundColor: 'var(--bg-surface-variant)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 500 }}>Active (Forwarding)</div>
                        <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--google-green)', marginTop: '4px' }}>{maskedEmails.filter(m => m.is_active).length}</div>
                      </div>
                      <div style={{ padding: '14px', backgroundColor: 'var(--bg-surface-variant)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)' }}>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 500 }}>Paused / Spam Blocked</div>
                        <div style={{ fontSize: '22px', fontWeight: 700, color: '#ef4444', marginTop: '4px' }}>{maskedEmails.filter(m => !m.is_active).length}</div>
                      </div>
                    </div>

                    {/* Search and Filters */}
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', backgroundColor: 'var(--bg-surface-variant)', borderRadius: 'var(--radius-pill)', border: '1px solid var(--border-color)', flex: 1, minWidth: '220px' }}>
                        <Search size={15} color="var(--text-muted)" />
                        <input
                          type="text"
                          placeholder="Search masks by label, address, or service..."
                          value={maskSearchQuery}
                          onChange={e => setMaskSearchQuery(e.target.value)}
                          style={{ background: 'none', border: 'none', outline: 'none', fontSize: '13px', color: 'var(--text-primary)', width: '100%' }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {['all', 'active', 'paused'].map(st => (
                          <button
                            key={st}
                            type="button"
                            onClick={() => setMaskFilterStatus(st)}
                            style={{
                              padding: '5px 12px',
                              borderRadius: 'var(--radius-pill)',
                              fontSize: '12px',
                              fontWeight: 600,
                              textTransform: 'capitalize',
                              cursor: 'pointer',
                              border: maskFilterStatus === st ? '1px solid #8b5cf6' : '1px solid var(--border-color)',
                              backgroundColor: maskFilterStatus === st ? 'rgba(139, 92, 246, 0.15)' : 'transparent',
                              color: maskFilterStatus === st ? '#8b5cf6' : 'var(--text-secondary)'
                            }}
                          >
                            {st}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Masked Emails List */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {maskedEmails
                        .filter(m => {
                          if (maskFilterStatus === 'active') return m.is_active;
                          if (maskFilterStatus === 'paused') return !m.is_active;
                          return true;
                        })
                        .filter(m => {
                          if (!maskSearchQuery.trim()) return true;
                          const q = maskSearchQuery.toLowerCase();
                          return m.alias_email.toLowerCase().includes(q) || m.label.toLowerCase().includes(q) || (m.description || '').toLowerCase().includes(q);
                        })
                        .length === 0 ? (
                        <div style={{ padding: '32px 16px', textAlign: 'center', backgroundColor: 'var(--bg-surface-variant)', borderRadius: 'var(--radius-md)', border: '1px dashed var(--border-color)' }}>
                          <Shield size={32} color="var(--text-muted)" style={{ margin: '0 auto 8px', opacity: 0.6 }} />
                          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>No masked aliases found</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>Click "Generate Masked Email" above to create your first disposable alias.</div>
                        </div>
                      ) : (
                        maskedEmails
                          .filter(m => {
                            if (maskFilterStatus === 'active') return m.is_active;
                            if (maskFilterStatus === 'paused') return !m.is_active;
                            return true;
                          })
                          .filter(m => {
                            if (!maskSearchQuery.trim()) return true;
                            const q = maskSearchQuery.toLowerCase();
                            return m.alias_email.toLowerCase().includes(q) || m.label.toLowerCase().includes(q) || (m.description || '').toLowerCase().includes(q);
                          })
                          .map(mask => (
                            <div
                              key={mask.id}
                              style={{
                                padding: '16px',
                                backgroundColor: 'var(--bg-surface-variant)',
                                borderRadius: 'var(--radius-md)',
                                border: mask.is_active ? '1px solid var(--border-color)' : '1px solid rgba(239, 68, 68, 0.4)',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '16px',
                                flexWrap: 'wrap',
                                opacity: mask.is_active ? 1 : 0.85
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', minWidth: '260px' }}>
                                <div style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '8px',
                                  backgroundColor: mask.is_active ? 'rgba(139, 92, 246, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                  color: mask.is_active ? '#8b5cf6' : '#ef4444',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0
                                }}>
                                  <Shield size={18} />
                                </div>
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <strong style={{ fontSize: '15px', fontFamily: 'monospace', color: 'var(--text-primary)' }}>{mask.alias_email}</strong>
                                    <button
                                      type="button"
                                      onClick={() => handleCopyMask(mask.alias_email, mask.id)}
                                      style={{
                                        border: 'none',
                                        background: 'none',
                                        cursor: 'pointer',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        backgroundColor: copiedMaskId === mask.id ? 'rgba(16, 185, 129, 0.2)' : 'var(--bg-surface)',
                                        color: copiedMaskId === mask.id ? 'var(--google-green)' : 'var(--text-secondary)'
                                      }}
                                      title="Copy masked address"
                                    >
                                      {copiedMaskId === mask.id ? <Check size={12} /> : <Copy size={12} />}
                                      <span>{copiedMaskId === mask.id ? 'Copied' : 'Copy'}</span>
                                    </button>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                                    <span style={{
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      padding: '2px 8px',
                                      borderRadius: 'var(--radius-pill)',
                                      backgroundColor: 'rgba(99, 102, 241, 0.12)',
                                      color: 'var(--primary)'
                                    }}>
                                      {mask.label}
                                    </span>
                                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                      Delivers to: <strong style={{ color: 'var(--text-secondary)' }}>{mask.forward_to}</strong>
                                    </span>
                                    {mask.description && (
                                      <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                        &bull; {mask.description}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                                  <span style={{
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    padding: '2px 8px',
                                    borderRadius: 'var(--radius-pill)',
                                    backgroundColor: mask.is_active ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                    color: mask.is_active ? 'var(--google-green)' : '#ef4444'
                                  }}>
                                    {mask.is_active ? '● FORWARDING' : '■ BLOCKED / PAUSED'}
                                  </span>
                                  {mask.expires_at && (
                                    <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                                      Expires {new Date(mask.expires_at).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>

                                <button
                                  type="button"
                                  onClick={() => handleToggleMaskedEmail(mask.id)}
                                  style={{
                                    padding: '6px 12px',
                                    borderRadius: 'var(--radius-sm)',
                                    border: '1px solid var(--border-color)',
                                    backgroundColor: mask.is_active ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                                    color: mask.is_active ? '#ef4444' : 'var(--google-green)',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                  }}
                                  title={mask.is_active ? "Block all incoming emails to this mask" : "Reactivate forwarding"}
                                >
                                  {mask.is_active ? 'Pause / Block' : 'Activate'}
                                </button>

                                <button
                                  type="button"
                                  className="gmail-icon-btn"
                                  onClick={() => handleDeleteMaskedEmail(mask.id, mask.alias_email)}
                                  style={{ color: 'var(--text-muted)' }}
                                  title="Permanently delete mask"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                          ))
                      )}
                    </div>
                  </div>
                )}

                {/* 5. LINK CLOUD STORAGE (MEGA & CLOUD DRIVES) */}
                {activeSettingsTab === 'cloud_storage' && (
                  <div className="gmail-settings-card">
                    <h3 style={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Cloud size={18} color="var(--google-blue)" />
                      <span>Link External Cloud Storage</span>
                    </h3>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                      Connect external cloud storage drives to expand your quota and automatically pool cloud space into your mailbox.
                    </p>

                    {/* MEGA Integration Card */}
                    <div style={{
                      padding: '20px',
                      backgroundColor: 'var(--bg-surface-variant)',
                      borderRadius: 'var(--radius-md)',
                      border: userSettings.mega_connected ? '2px solid var(--google-green)' : '1px solid var(--border-color)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ backgroundColor: '#cc0000', color: 'white', width: '28px', height: '28px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '14px' }}>M</span>
                          <div>
                            <strong style={{ fontSize: '15px' }}>MEGA Cloud Drive</strong>
                            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Secure end-to-end encrypted storage pool</div>
                          </div>
                        </div>
                        <span style={{
                          fontSize: '11px',
                          fontWeight: 700,
                          padding: '3px 10px',
                          borderRadius: 'var(--radius-pill)',
                          backgroundColor: userSettings.mega_connected ? 'var(--google-green)' : 'var(--border-color)',
                          color: '#ffffff'
                        }}>
                          {userSettings.mega_connected ? 'CONNECTED' : 'NOT LINKED'}
                        </span>
                      </div>

                      {userSettings.mega_connected ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <div style={{ fontSize: '13px' }}>
                            Linked Account: <strong>{userSettings.mega_email}</strong>
                          </div>
                          <div style={{ fontSize: '13px' }}>
                            Storage Pool: <strong>{(userSettings.mega_storage_total / (1024 * 1024 * 1024)).toFixed(1)} GB</strong>
                          </div>
                          <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
                            <button className="gmail-pill-btn primary" onClick={handleSyncMega} disabled={isSyncingMega}>
                              <RefreshCw size={14} className={isSyncingMega ? 'spinning' : ''} />
                              <span>{isSyncingMega ? 'Syncing...' : 'Sync Now'}</span>
                            </button>
                            <button className="gmail-pill-btn" style={{ color: 'var(--google-red)' }} onClick={handleUnlinkMega}>
                              Unlink Account
                            </button>
                          </div>
                        </div>
                      ) : (
                        <form onSubmit={handleConnectMega} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <div className="gmail-input-group">
                            <label>MEGA Account Email</label>
                            <input
                              type="email"
                              className="gmail-form-input"
                              placeholder="user@mega.nz"
                              value={megaEmailInput}
                              onChange={e => setMegaEmailInput(e.target.value)}
                              required
                            />
                          </div>
                          <div className="gmail-input-group">
                            <label>MEGA Account Password</label>
                            <input
                              type="password"
                              className="gmail-form-input"
                              placeholder="••••••••••••"
                              value={megaPasswordInput}
                              onChange={e => setMegaPasswordInput(e.target.value)}
                              required
                            />
                          </div>
                          <button type="submit" className="gmail-pill-btn primary" style={{ alignSelf: 'flex-start' }} disabled={isLinkingMega}>
                            {isLinkingMega ? 'Authenticating...' : 'Connect MEGA Account'}
                          </button>
                        </form>
                      )}
                    </div>
                  </div>
                )}

                {/* 6. SIGNATURE */}
                {activeSettingsTab === 'signature' && (
                  <form onSubmit={handleSaveSettings} className="gmail-settings-card">
                    <h3 style={{ fontSize: '16px', fontWeight: 600 }}>Email Signature</h3>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Appended to outgoing messages automatically.</p>
                    <textarea
                      className="gmail-compose-body"
                      style={{ border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', minHeight: '120px' }}
                      value={userSettings.signature || ''}
                      onChange={e => setUserSettings({ ...userSettings, signature: e.target.value })}
                      placeholder="Best regards,&#10;Your Name"
                    />
                    <button type="submit" className="gmail-pill-btn primary" style={{ alignSelf: 'flex-start' }}>Save Signature</button>
                  </form>
                )}

                {/* 7. AUTOREPLY */}
                {activeSettingsTab === 'autoreply' && (
                  <form onSubmit={handleSaveSettings} className="gmail-settings-card">
                    <h3 style={{ fontSize: '16px', fontWeight: 600 }}>Vacation Responder / Auto-Reply</h3>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={userSettings.autoresponder_enabled}
                        onChange={e => setUserSettings({ ...userSettings, autoresponder_enabled: e.target.checked })}
                      />
                      <span>Enable Vacation Auto-Responder</span>
                    </label>
                    <div className="gmail-input-group">
                      <label>Subject</label>
                      <input
                        className="gmail-form-input"
                        value={userSettings.autoresponder_subject || ''}
                        onChange={e => setUserSettings({ ...userSettings, autoresponder_subject: e.target.value })}
                        placeholder="Out of office"
                      />
                    </div>
                    <div className="gmail-input-group">
                      <label>Message</label>
                      <textarea
                        className="gmail-compose-body"
                        style={{ border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', minHeight: '100px' }}
                        value={userSettings.autoresponder_body || ''}
                        onChange={e => setUserSettings({ ...userSettings, autoresponder_body: e.target.value })}
                        placeholder="I am currently away and will reply upon return."
                      />
                    </div>
                    <button type="submit" className="gmail-pill-btn primary" style={{ alignSelf: 'flex-start' }}>Save Responder</button>
                  </form>
                )}

                {/* 8. SECURITY */}
                {activeSettingsTab === 'security' && (
                  <div className="gmail-settings-card">
                    <h3 style={{ fontSize: '16px', fontWeight: 600 }}>Change Password</h3>
                    <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div className="gmail-input-group">
                        <label>Current Password</label>
                        <input
                          type="password"
                          className="gmail-form-input"
                          value={currentPassInput}
                          onChange={e => setCurrentPassInput(e.target.value)}
                          required
                        />
                      </div>
                      <div className="gmail-input-group">
                        <label>New Password</label>
                        <input
                          type="password"
                          className="gmail-form-input"
                          value={newPassInput}
                          onChange={e => setNewPassInput(e.target.value)}
                          required
                        />
                      </div>
                      <button type="submit" className="gmail-pill-btn primary" style={{ alignSelf: 'flex-start' }}>Update Password</button>
                    </form>
                  </div>
                )}

                {/* 9. APPEARANCE & ACCENT */}
                {activeSettingsTab === 'appearance' && (
                  <div className="gmail-settings-card">
                    <h3 style={{ fontSize: '16px', fontWeight: 600 }}>Themes & Custom Styling</h3>
                    
                    <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-muted)' }}>Base Theme</span>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                      {[
                        { id: 'light', name: 'Google Light', icon: <Sun size={20} /> },
                        { id: 'dark', name: 'Google Dark', icon: <Moon size={20} /> },
                        { id: 'glass', name: 'Vibrant Glass', icon: <Sparkles size={20} /> }
                      ].map(th => (
                        <div
                          key={th.id}
                          onClick={() => setTheme(th.id)}
                          style={{
                            padding: '16px 24px',
                            border: `2px solid ${theme === th.id ? 'var(--google-blue)' : 'var(--border-color)'}`,
                            borderRadius: 'var(--radius-md)',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                            backgroundColor: 'var(--bg-surface)'
                          }}
                        >
                          {th.icon}
                          <span style={{ fontWeight: 500, fontSize: '14px' }}>{th.name}</span>
                          {theme === th.id && <span style={{ color: 'var(--google-blue)', fontSize: '11px', fontWeight: 700 }}>ACTIVE</span>}
                        </div>
                      ))}
                    </div>

                    <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-muted)', marginTop: '12px' }}>Workspace Accent Color</span>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      {[
                        { id: 'blue', color: '#0b57d0', name: 'Blue' },
                        { id: 'emerald', color: '#10b981', name: 'Emerald' },
                        { id: 'purple', color: '#8b5cf6', name: 'Purple' },
                        { id: 'amber', color: '#f59e0b', name: 'Amber' },
                        { id: 'crimson', color: '#ef4444', name: 'Crimson' }
                      ].map(acc => (
                        <button
                          key={acc.id}
                          onClick={() => setAccentColor(acc.id)}
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            backgroundColor: acc.color,
                            border: accentColor === acc.id ? '3px solid var(--text-primary)' : '2px solid transparent',
                            cursor: 'pointer'
                          }}
                          title={acc.name}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* 10. STORAGE */}
                {activeSettingsTab === 'storage' && (
                  <div className="gmail-settings-card">
                    <h3 style={{ fontSize: '16px', fontWeight: 600 }}>Storage & Quota Allocation</h3>
                    <div className="gmail-progress-bar" style={{ height: '10px' }}>
                      <div className="gmail-progress-fill" style={{ width: `${storageUsage.percent}%` }} />
                    </div>
                    <span>
                      {storageUsage.totalMb >= 1024
                        ? `${(storageUsage.usedMb / 1024).toFixed(2)} GB used of ${(storageUsage.totalMb / 1024).toFixed(1)} GB total allocation (${storageUsage.percent}%)`
                        : `${storageUsage.usedMb} MB used of ${storageUsage.totalMb} MB total allocation (${storageUsage.percent}%)`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </main>
        )}
      </div>

      {/* 3. DOCKED GMAIL COMPOSE BOX */}
      {composeState.isOpen && (
        <div className={`gmail-compose-dock ${composeState.isMinimized ? 'minimized' : ''} ${composeState.isMaximized ? 'maximized' : ''}`}>
          <div
            className="gmail-compose-header"
            onClick={() => setComposeState(prev => ({ ...prev, isMinimized: !prev.isMinimized }))}
          >
            <span>{composeState.isEditingScheduled ? `⏱️ Edit Scheduled Email — ${composeState.subject || '(no subject)'}` : (composeState.subject || 'New Message')}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} onClick={e => e.stopPropagation()}>
              <button
                className="gmail-icon-btn"
                style={{ width: '28px', height: '28px' }}
                onClick={() => setComposeState(prev => ({ ...prev, isMinimized: !prev.isMinimized }))}
              >
                <Minimize2 size={14} />
              </button>
              <button
                className="gmail-icon-btn"
                style={{ width: '28px', height: '28px' }}
                onClick={() => setComposeState(prev => ({ ...prev, isMaximized: !prev.isMaximized }))}
              >
                <Maximize2 size={14} />
              </button>
              <button
                className="gmail-icon-btn"
                style={{ width: '28px', height: '28px' }}
                onClick={() => setComposeState(prev => ({ ...prev, isOpen: false, isEditingScheduled: false, scheduledId: null, scheduledFor: null }))}
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {!composeState.isMinimized && (
            <>
              {composeState.isEditingScheduled && composeState.scheduledFor && (
                <div style={{ backgroundColor: 'rgba(26, 115, 232, 0.08)', borderBottom: '1px solid rgba(26, 115, 232, 0.25)', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--google-blue)', fontSize: '13px', fontWeight: 600 }}>
                    <Clock size={16} />
                    <span>Scheduled for: {new Date(composeState.scheduledFor).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Change Time:</span>
                    <input
                      type="datetime-local"
                      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', padding: '4px 8px', borderRadius: '6px', fontSize: '12px' }}
                      value={composeState.scheduledFor ? new Date(new Date(composeState.scheduledFor).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''}
                      onChange={e => {
                        if (e.target.value) {
                          setComposeState(prev => ({ ...prev, scheduledFor: new Date(e.target.value).toISOString() }));
                        }
                      }}
                    />
                  </div>
                </div>
              )}
              <div className="gmail-compose-fields">
                <div className="gmail-compose-row" style={{ position: 'relative' }}>
                  <label>To</label>
                  <input
                    type="text"
                    placeholder="Recipients (separate multiple with commas)"
                    value={composeState.to}
                    onChange={e => setComposeState(prev => ({ ...prev, to: e.target.value }))}
                  />
                  <button
                    type="button"
                    className="gmail-icon-btn"
                    style={{ width: '26px', height: '26px', padding: 0, marginRight: '6px' }}
                    onClick={() => { setContactTargetField('to'); setIsContactPickerOpen(prev => !prev); }}
                    title="Insert contact from address book"
                  >
                    <Users size={15} color="var(--google-blue)" />
                  </button>
                  <span
                    style={{ fontSize: '12px', color: 'var(--text-muted)', cursor: 'pointer', marginRight: '8px' }}
                    onClick={() => setShowCc(!showCc)}
                  >
                    Cc
                  </span>
                  <span
                    style={{ fontSize: '12px', color: 'var(--text-muted)', cursor: 'pointer' }}
                    onClick={() => setShowBcc(!showBcc)}
                  >
                    Bcc
                  </span>

                  {/* Contact Picker Dropdown */}
                  {isContactPickerOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        zIndex: 1100,
                        backgroundColor: 'var(--bg-surface)',
                        border: '1px solid var(--border-color)',
                        borderRadius: 'var(--radius-md)',
                        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)',
                        width: '260px',
                        maxHeight: '220px',
                        overflowY: 'auto',
                        padding: '6px'
                      }}
                      onClick={e => e.stopPropagation()}
                    >
                      <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', padding: '4px 8px' }}>
                        Select contact to add to {contactTargetField.toUpperCase()}:
                      </div>
                      {contacts.length === 0 ? (
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', padding: '8px', textAlign: 'center' }}>
                          No saved contacts yet.
                        </div>
                      ) : (
                        contacts.map(c => (
                          <div
                            key={c.id}
                            onClick={() => {
                              const currentVal = composeState[contactTargetField];
                              const newVal = currentVal ? `${currentVal}, ${c.email}` : c.email;
                              setComposeState(prev => ({ ...prev, [contactTargetField]: newVal }));
                              setIsContactPickerOpen(false);
                            }}
                            style={{
                              padding: '6px 8px',
                              borderRadius: 'var(--radius-sm)',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '12px'
                            }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <GmailAvatar email={c.email} name={c.name} size={22} />
                            <div style={{ overflow: 'hidden' }}>
                              <div style={{ fontWeight: 600, textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{c.email}</div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {showCc && (
                  <div className="gmail-compose-row">
                    <label>Cc</label>
                    <input
                      type="text"
                      placeholder="Carbon copy"
                      value={composeState.cc}
                      onChange={e => setComposeState(prev => ({ ...prev, cc: e.target.value }))}
                    />
                    <button
                      type="button"
                      className="gmail-icon-btn"
                      style={{ width: '26px', height: '26px', padding: 0 }}
                      onClick={() => { setContactTargetField('cc'); setIsContactPickerOpen(prev => !prev); }}
                      title="Insert contact"
                    >
                      <Users size={14} color="var(--google-blue)" />
                    </button>
                  </div>
                )}

                {showBcc && (
                  <div className="gmail-compose-row">
                    <label>Bcc</label>
                    <input
                      type="text"
                      placeholder="Blind carbon copy"
                      value={composeState.bcc}
                      onChange={e => setComposeState(prev => ({ ...prev, bcc: e.target.value }))}
                    />
                    <button
                      type="button"
                      className="gmail-icon-btn"
                      style={{ width: '26px', height: '26px', padding: 0 }}
                      onClick={() => { setContactTargetField('bcc'); setIsContactPickerOpen(prev => !prev); }}
                      title="Insert contact"
                    >
                      <Users size={14} color="var(--google-blue)" />
                    </button>
                  </div>
                )}

                <div className="gmail-compose-row">
                  <input
                    type="text"
                    placeholder="Subject"
                    style={{ fontWeight: 500 }}
                    value={composeState.subject}
                    onChange={e => setComposeState(prev => ({ ...prev, subject: e.target.value }))}
                  />
                </div>
              </div>

              {composeState.attachments.length > 0 && (
                <div style={{ padding: '8px 16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {composeState.attachments.map((att, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 10px',
                        backgroundColor: 'var(--bg-surface-variant)',
                        borderRadius: 'var(--radius-pill)',
                        fontSize: '12px'
                      }}
                    >
                      <Paperclip size={12} />
                      <span>{att.filename}</span>
                      <button onClick={() => setComposeState(prev => ({ ...prev, attachments: prev.attachments.filter((_, idx) => idx !== i) }))}>
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <textarea
                className="gmail-compose-body"
                placeholder="Write your message here... (Ctrl+Enter to send)"
                value={composeState.body}
                onKeyDown={e => {
                  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                    e.preventDefault();
                    handleSendEmail();
                  }
                }}
                onChange={e => setComposeState(prev => ({ ...prev, body: e.target.value }))}
              />

              <div className="gmail-compose-footer">
                {composeState.isEditingScheduled ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button
                      className="gmail-btn-primary"
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: 'var(--radius-pill)', fontWeight: 600 }}
                      onClick={() => handleUpdateScheduledEmail()}
                      disabled={composeState.isSending}
                    >
                      <Clock size={15} />
                      <span>{composeState.isSending ? 'Updating...' : 'Save & Reschedule'}</span>
                    </button>

                    <button
                      type="button"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 14px',
                        borderRadius: 'var(--radius-pill)',
                        border: '1px solid var(--google-blue)',
                        backgroundColor: 'rgba(26, 115, 232, 0.1)',
                        color: 'var(--google-blue)',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 600
                      }}
                      onClick={() => {
                        handleSendScheduledNow(composeState.scheduledId);
                        setComposeState(prev => ({ ...prev, isOpen: false, isEditingScheduled: false, scheduledId: null, scheduledFor: null }));
                      }}
                      disabled={composeState.isSending}
                      title="Send this email immediately"
                    >
                      <Send size={14} />
                      <span>Send Now</span>
                    </button>

                    <button
                      type="button"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 14px',
                        borderRadius: 'var(--radius-pill)',
                        border: '1px solid var(--border-subtle)',
                        backgroundColor: 'transparent',
                        color: 'var(--google-red)',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 500
                      }}
                      onClick={() => {
                        handleCancelScheduledEmail(composeState.scheduledId);
                        setComposeState(prev => ({ ...prev, isOpen: false, isEditingScheduled: false, scheduledId: null, scheduledFor: null }));
                      }}
                      title="Cancel and delete this scheduled email"
                    >
                      <Trash2 size={14} />
                      <span>Cancel Schedule</span>
                    </button>

                    <label className="gmail-icon-btn" style={{ cursor: 'pointer' }} title="Attach files">
                      <Paperclip size={18} />
                      <input type="file" multiple style={{ display: 'none' }} onChange={handleFileAttach} />
                    </label>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {/* Split Send & Schedule Send */}
                    <div style={{ position: 'relative' }}>
                      <div className="gmail-split-send">
                        <button
                          className="gmail-split-send-main"
                          onClick={handleSendEmail}
                          disabled={composeState.isSending}
                        >
                          <Send size={15} />
                          <span>{composeState.isSending ? 'Sending...' : 'Send'}</span>
                        </button>
                        <button
                          type="button"
                          className="gmail-split-send-arrow"
                          onClick={() => setIsSchedulePopoverOpen(prev => !prev)}
                          title="Schedule send (Send later)"
                        >
                          <Clock size={14} />
                        </button>
                      </div>

                      {isSchedulePopoverOpen && (
                        <div className="gmail-schedule-popover" onClick={e => e.stopPropagation()}>
                          <div style={{ padding: '8px 16px 6px', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', borderBottom: '1px solid var(--border-subtle)' }}>
                            Schedule send
                          </div>

                          <div
                            className="gmail-schedule-option"
                            onClick={() => {
                              const d = new Date();
                              d.setDate(d.getDate() + 1);
                              d.setHours(8, 0, 0, 0);
                              handleScheduleEmail(d.toISOString());
                            }}
                          >
                            <span>🌅 Tomorrow morning</span>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>8:00 AM</span>
                          </div>

                          <div
                            className="gmail-schedule-option"
                            onClick={() => {
                              const d = new Date();
                              d.setDate(d.getDate() + 1);
                              d.setHours(13, 0, 0, 0);
                              handleScheduleEmail(d.toISOString());
                            }}
                          >
                            <span>☀️ Tomorrow afternoon</span>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>1:00 PM</span>
                          </div>

                          <div
                            className="gmail-schedule-option"
                            onClick={() => {
                              const d = new Date();
                              const day = d.getDay();
                              const diff = (day === 0 ? 1 : (8 - day));
                              d.setDate(d.getDate() + diff);
                              d.setHours(8, 0, 0, 0);
                              handleScheduleEmail(d.toISOString());
                            }}
                          >
                            <span>📅 Monday morning</span>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>8:00 AM</span>
                          </div>

                          <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '10px 14px 6px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>Pick custom time:</div>
                            <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                              <input
                                type="date"
                                style={{ flex: 1, padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
                                value={customScheduleDate}
                                onChange={e => setCustomScheduleDate(e.target.value)}
                              />
                              <input
                                type="time"
                                style={{ width: '80px', padding: '4px 6px', fontSize: '12px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
                                value={customScheduleTime}
                                onChange={e => setCustomScheduleTime(e.target.value)}
                              />
                            </div>
                            <button
                              type="button"
                              style={{
                                width: '100%',
                                padding: '6px',
                                backgroundColor: 'var(--google-blue)',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: 600,
                                cursor: 'pointer'
                              }}
                              onClick={() => {
                                if (!customScheduleDate) {
                                  showToast('Please choose a date', 'warning');
                                  return;
                                }
                                const dt = new Date(`${customScheduleDate}T${customScheduleTime}:00`);
                                handleScheduleEmail(dt.toISOString());
                              }}
                            >
                              Schedule Delivery
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 14px',
                        borderRadius: 'var(--radius-pill)',
                        border: '1px solid var(--border-subtle)',
                        backgroundColor: 'var(--bg-surface-variant)',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 500,
                        transition: 'all var(--transition-fast)'
                      }}
                      onClick={handleSaveDraft}
                      disabled={composeState.isSavingDraft || composeState.isSending}
                      title="Save draft to Drafts folder"
                    >
                      <Bookmark size={15} />
                      <span>{composeState.isSavingDraft ? 'Saving...' : 'Save Draft'}</span>
                    </button>

                    <label className="gmail-icon-btn" style={{ cursor: 'pointer' }} title="Attach files">
                      <Paperclip size={18} />
                      <input type="file" multiple style={{ display: 'none' }} onChange={handleFileAttach} />
                    </label>
                  </div>
                )}

                <button
                  className="gmail-icon-btn"
                  onClick={() => setComposeState(prev => ({ ...prev, isOpen: false }))}
                  title="Discard draft"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* 4. CREATE EVENT MODAL */}
      {isAddEventOpen && (
        <div className="gmail-modal-backdrop" onClick={() => setIsAddEventOpen(false)}>
          <div className="gmail-modal-card" onClick={e => e.stopPropagation()}>
            <div className="gmail-modal-header">
              <span>Schedule Calendar Event</span>
              <button className="gmail-icon-btn" onClick={() => setIsAddEventOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleAddEvent}>
              <div className="gmail-modal-body">
                <div className="gmail-input-group">
                  <label>Event Title</label>
                  <input
                    type="text"
                    className="gmail-form-input"
                    placeholder="e.g. Project Review Meeting"
                    value={newEventTitle}
                    onChange={e => setNewEventTitle(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <div className="gmail-input-group">
                  <label>Date</label>
                  <input
                    type="date"
                    className="gmail-form-input"
                    value={newEventDate}
                    onChange={e => setNewEventDate(e.target.value)}
                    required
                  />
                </div>
                <div className="gmail-input-group">
                  <label>Category</label>
                  <select
                    className="gmail-form-input"
                    value={newEventType}
                    onChange={e => setNewEventType(e.target.value)}
                  >
                    <option value="event">Meeting / Event</option>
                    <option value="reminder">Reminder</option>
                    <option value="birthday">Birthday / Milestone</option>
                  </select>
                </div>
              </div>
              <div className="gmail-modal-footer">
                <button type="button" className="gmail-pill-btn" onClick={() => setIsAddEventOpen(false)}>Cancel</button>
                <button type="submit" className="gmail-send-btn">Save Event</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 5. ADD CONTACT MODAL */}
      {isAddContactOpen && (
        <div className="gmail-modal-backdrop" onClick={() => setIsAddContactOpen(false)}>
          <div className="gmail-modal-card" onClick={e => e.stopPropagation()}>
            <div className="gmail-modal-header">
              <span>New Contact</span>
              <button className="gmail-icon-btn" onClick={() => setIsAddContactOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleAddContact}>
              <div className="gmail-modal-body">
                <div className="gmail-input-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    className="gmail-form-input"
                    placeholder="e.g. John Doe"
                    value={newContactName}
                    onChange={e => setNewContactName(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="gmail-input-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    className="gmail-form-input"
                    placeholder="john@example.com"
                    value={newContactEmail}
                    onChange={e => setNewContactEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="gmail-input-group">
                  <label>Phone Number</label>
                  <input
                    type="text"
                    className="gmail-form-input"
                    placeholder="+1 (555) 000-0000"
                    value={newContactPhone}
                    onChange={e => setNewContactPhone(e.target.value)}
                  />
                </div>
              </div>
              <div className="gmail-modal-footer">
                <button type="button" className="gmail-pill-btn" onClick={() => setIsAddContactOpen(false)}>Cancel</button>
                <button type="submit" className="gmail-send-btn">Save Contact</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 6. CREATE FOLDER MODAL */}
      {isNewFolderOpen && (
        <div className="gmail-modal-backdrop" onClick={() => setIsNewFolderOpen(false)}>
          <div className="gmail-modal-card" onClick={e => e.stopPropagation()}>
            <div className="gmail-modal-header">
              <span>New Folder</span>
              <button className="gmail-icon-btn" onClick={() => setIsNewFolderOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const folderName = e.target.folderName.value.trim();
              if (!folderName) return;
              try {
                const res = await fetch(`${API_BASE}/mail/folders`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ path: `INBOX.${folderName}` })
                });
                if (res.ok) {
                  showToast(`Folder "${folderName}" created`, 'success');
                  setIsNewFolderOpen(false);
                  fetchFolders();
                } else {
                  showToast('Failed to create folder', 'danger');
                }
              } catch (err) {
                showToast('Error creating folder', 'danger');
              }
            }}>
              <div className="gmail-modal-body">
                <div className="gmail-input-group">
                  <label>Folder Name:</label>
                  <input name="folderName" type="text" className="gmail-form-input" placeholder="e.g. Invoices, Archive 2026" autoFocus required />
                </div>
              </div>
              <div className="gmail-modal-footer">
                <button type="button" className="gmail-pill-btn" onClick={() => setIsNewFolderOpen(false)}>Cancel</button>
                <button type="submit" className="gmail-send-btn">Create Folder</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 7. CREATE / EDIT LABEL MODAL */}
      {isLabelModalOpen && (
        <div className="gmail-modal-backdrop" onClick={() => setIsLabelModalOpen(false)}>
          <div className="gmail-modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="gmail-modal-header">
              <span>{editingLabel ? 'Edit Label' : 'New Label'}</span>
              <button className="gmail-icon-btn" onClick={() => setIsLabelModalOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSaveLabel}>
              <div className="gmail-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div className="gmail-input-group">
                  <label>Label Name</label>
                  <input
                    type="text"
                    className="gmail-form-input"
                    placeholder="e.g. Work, Urgent, Project X, Finance"
                    value={labelForm.name}
                    onChange={e => setLabelForm(prev => ({ ...prev, name: e.target.value }))}
                    autoFocus
                    required
                  />
                </div>

                <div className="gmail-input-group">
                  <label>Label Color</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '10px', marginTop: '4px' }}>
                    {LABEL_COLORS.map(c => (
                      <button
                        type="button"
                        key={c.hex}
                        onClick={() => setLabelForm(prev => ({ ...prev, color: c.hex }))}
                        style={{
                          width: '38px',
                          height: '38px',
                          borderRadius: '50%',
                          backgroundColor: c.hex,
                          border: labelForm.color === c.hex ? '3px solid #fff' : '2px solid transparent',
                          boxShadow: labelForm.color === c.hex ? `0 0 0 2px ${c.hex}` : 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          transition: 'all 0.15s ease'
                        }}
                        title={c.name}
                      >
                        {labelForm.color === c.hex && <Check size={16} strokeWidth={3} />}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="gmail-input-group">
                  <label>Preview</label>
                  <div style={{ padding: '12px', backgroundColor: 'var(--bg-surface-variant)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center' }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 12px',
                        borderRadius: 'var(--radius-pill)',
                        fontSize: '13px',
                        fontWeight: 600,
                        backgroundColor: `${labelForm.color}20`,
                        color: labelForm.color,
                        border: `1px solid ${labelForm.color}60`
                      }}
                    >
                      <Tag size={13} />
                      <span>{labelForm.name.trim() || 'Label Preview'}</span>
                    </span>
                  </div>
                </div>

                {userEmail && labelForm.name.trim() && (
                  <div style={{
                    padding: '12px 14px',
                    backgroundColor: 'var(--bg-surface-variant)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px dashed var(--border-color)',
                    fontSize: '12px',
                    color: 'var(--text-secondary)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px'
                  }}>
                    <span style={{ fontWeight: 600, color: 'var(--google-blue)', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Sparkles size={13} /> Plus-Addressing Subaddress:
                    </span>
                    <span>
                      Give out <code style={{ backgroundColor: 'var(--bg-card)', padding: '2px 6px', borderRadius: '4px', color: 'var(--primary)', fontWeight: 600 }}>{userEmail.split('@')[0]}+{labelForm.name.trim().toLowerCase().replace(/[^a-z0-9]/g, '')}@{userEmail.split('@')[1]}</code> to automatically route and tag incoming emails with this label!
                    </span>
                  </div>
                )}
              </div>

              <div className="gmail-modal-footer">
                <button
                  type="button"
                  className="gmail-pill-btn"
                  onClick={() => setIsLabelModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="gmail-send-btn"
                >
                  {editingLabel ? 'Save Changes' : 'Create Label'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 7b. GENERATE DISPOSABLE MASKED EMAIL MODAL */}
      {isMaskModalOpen && (
        <div className="gmail-modal-backdrop" onClick={() => setIsMaskModalOpen(false)}>
          <div className="gmail-modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="gmail-modal-header">
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Shield size={18} color="#8b5cf6" />
                <span>Generate Disposable Masked Email</span>
              </span>
              <button className="gmail-icon-btn" onClick={() => setIsMaskModalOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleCreateMaskedEmail}>
              <div className="gmail-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div className="gmail-input-group">
                  <label>Service / Website Label *</label>
                  <input
                    type="text"
                    className="gmail-form-input"
                    placeholder="e.g. Amazon, Nike, Reddit, Crypto Forum"
                    value={maskForm.label}
                    onChange={e => setMaskForm(prev => ({ ...prev, label: e.target.value }))}
                    autoFocus
                    required
                  />
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Name the service you are giving this address to.</span>
                </div>

                <div className="gmail-input-group">
                  <label>Custom Prefix (Optional)</label>
                  <input
                    type="text"
                    className="gmail-form-input"
                    placeholder="e.g. shop, social, secure (leaves blank for auto)"
                    value={maskForm.prefix}
                    onChange={e => setMaskForm(prev => ({ ...prev, prefix: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') }))}
                  />
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>A unique random code will be appended to ensure uniqueness.</span>
                </div>

                <div className="gmail-input-group">
                  <label>Domain</label>
                  <select
                    className="gmail-form-input"
                    value={maskForm.domain}
                    onChange={e => setMaskForm(prev => ({ ...prev, domain: e.target.value }))}
                  >
                    <option value={userEmail.split('@')[1] || 'mainnode.qzz.io'}>@{userEmail.split('@')[1] || 'mainnode.qzz.io'}</option>
                    <option value="mainnode.qzz.io">@mainnode.qzz.io</option>
                    <option value="mainnode.online">@mainnode.online</option>
                    <option value="nodemaster.dpdns.org">@nodemaster.dpdns.org</option>
                  </select>
                </div>

                <div className="gmail-input-group">
                  <label>Notes / Description (Optional)</label>
                  <input
                    type="text"
                    className="gmail-form-input"
                    placeholder="e.g. Black Friday order, secondary account"
                    value={maskForm.description}
                    onChange={e => setMaskForm(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>

                <div className="gmail-input-group">
                  <label>Expiration Policy</label>
                  <select
                    className="gmail-form-input"
                    value={maskForm.expiresInDays}
                    onChange={e => setMaskForm(prev => ({ ...prev, expiresInDays: parseInt(e.target.value) || 0 }))}
                  >
                    <option value="0">Never expires (Permanent alias)</option>
                    <option value="7">Auto-expire in 7 days</option>
                    <option value="30">Auto-expire in 30 days</option>
                    <option value="90">Auto-expire in 90 days</option>
                    <option value="365">Auto-expire in 1 year</option>
                  </select>
                </div>

                {/* Preview Box */}
                <div style={{
                  padding: '14px',
                  backgroundColor: 'var(--bg-surface-variant)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px dashed var(--border-color)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px'
                }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Preview Destination</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Incoming mail will forward to: <strong style={{ color: 'var(--text-primary)' }}>{userEmail}</strong>
                  </div>
                  <div style={{ fontSize: '12px', color: '#8b5cf6', display: 'flex', alignItems: 'center', gap: '5px', marginTop: '4px' }}>
                    <ShieldCheck size={14} />
                    <span>Real email address will never be exposed to the sender</span>
                  </div>
                </div>
              </div>

              <div className="gmail-modal-footer">
                <button
                  type="button"
                  className="gmail-pill-btn"
                  onClick={() => setIsMaskModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="gmail-send-btn"
                  disabled={isGeneratingMask}
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #6366f1)', border: 'none' }}
                >
                  {isGeneratingMask ? 'Generating...' : 'Create Masked Email'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 8. TOAST NOTIFICATION */}
      {toast && (
        <div className="gmail-toast">
          <span>{toast.text}</span>
          <button onClick={() => setToast(null)} style={{ color: 'inherit' }}>
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
